import React, { useState, useMemo, useEffect } from 'react';
import {
  Bookmark,
  Download,
  Star,
  Search,
  SlidersHorizontal,
  RotateCcw,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  ArrowUpDown,
  Info,
  TrendingUp,
  X,
  Check,
  Loader2,
  AlertTriangle,
  GripVertical,
  Pin,
  Lock,
  Users,
} from 'lucide-react';
import { addDays, format as formatDate } from 'date-fns';
import { SAVED_VIEW_NAME_MAX_LENGTH, SavedViewRecord, SavedViewVisibility } from '../../../services/reports/SavedViews.service';
import { LocalSavedView, useSavedViews } from '../../../hooks/useSavedViews';
import TopSellersApi, { TopSellerApiRow, SkuVariantApiRow, DropdownOption, TopSellersHierarchy, TopSellersOtb, OtbMetrics } from '../../../services/reports/TopSellers.service';
import { OtbSummaryPills } from '../OtbSummaryPills.component';
import AuthProvider from '../../../providers/AuthProvider';
import { normalizeColumnOrder, useDraggableColumnOrder } from '../../../hooks/useDraggableColumnOrder';
import { COLUMN_RESIZE_HANDLE_CLASS, useResizableColumns } from '../../../hooks/useResizableColumns';
import { pinColumn, unpinColumn, usePinnedColumns } from '../../../hooks/columnPinning';
import { usePendingFilterSelection } from '../../../hooks/usePendingFilterSelection';
import { STICKY_LAYER, useStickyTableRows } from '../../../hooks/useStickyTableRows';
import { ColumnHeaderMenu, ColumnSortDirection, SORT_DIRECTION_LABELS } from '../ColumnHeaderMenu.component';
import { downloadCsv } from '../downloadCsv';
import { buildParentChildCsv } from '../csvParentChildRows';
import {
  AVG_PURCHASE_PRICE_DESCRIPTION,
  AVG_PURCHASE_PRICE_LABEL,
  AVG_SELLING_PRICE_DESCRIPTION,
  AVG_SELLING_PRICE_LABEL,
  SUB_CLASS_DESCRIPTION,
  SUB_CLASS_LABEL,
  avgPerUnit,
  resolveDateRange as resolveVendorPerformanceDateRange,
  CSV_PERIOD_LABELS,
  csvCell,
  fmtDiffUnitPrice,
  fmtUnitPrice,
  getSpanDateRangeInfo,
  ON_ORDER_NO_DATE_NOTE,
  ON_ORDER_OTB_SPAN_NOTE,
  ON_ORDER_SPAN_OPTIONS,
} from '../vendorPerformance/VendorPerformanceReport.component';

export type TopSellersView = 'total_store' | 'by_class' | 'by_product' | 'by_sku';
export type RankByMetric = 'sales_dollars' | 'sales_units' | 'gross_margin_pct' | 'gmroi';
export type SearchFieldMode = 'product_name' | 'tag' | 'sku';
export type { TopSellersHierarchy };
export type SkuGrain = 'product' | 'sku';

export interface HierarchyOptionConfig {
  key: TopSellersHierarchy;
  label: string;
  hasOtb: boolean;
}

export const WITH_OTB_HIERARCHY_OPTIONS: HierarchyOptionConfig[] = [
  { key: 'total', label: 'Total by Product', hasOtb: true },
  { key: 'category', label: 'Category by Product', hasOtb: true },
  { key: 'class', label: 'Class by Product', hasOtb: true },
];

// Plan data has no vendor grain, so Vendor by Product can't show open-to-buy.
export const WITHOUT_OTB_HIERARCHY_OPTIONS: HierarchyOptionConfig[] = [
  { key: 'vendor', label: 'Vendor by Product', hasOtb: false },
  { key: 'location', label: 'Product by Location', hasOtb: false },
];

export const HIERARCHY_OPTIONS: HierarchyOptionConfig[] = [...WITH_OTB_HIERARCHY_OPTIONS, ...WITHOUT_OTB_HIERARCHY_OPTIONS];

const HIERARCHY_GROUP_NOUN: Record<Exclude<TopSellersHierarchy, 'total'>, string> = {
  category: 'Category',
  class: 'Class',
  vendor: 'Vendor',
  location: 'Location',
};

// Saved views predating the Hierarchy control stored only a legacy `view`.
export const resolveSavedViewLayout = (sv: Pick<SavedViewItem, 'view' | 'hierarchy'>): { hierarchy: TopSellersHierarchy; skuGrain: SkuGrain } => ({
  hierarchy: sv.hierarchy ?? (sv.view === 'by_class' ? 'class' : 'total'),
  skuGrain: sv.view === 'by_sku' ? 'sku' : 'product',
});

// One SKU/variant nested under its parent product row in the "By SKU" view -- mirrors the metric
// columns a normal item row shows, since renderVariantRows() renders a full row per column, not a
// stripped-down placeholder.
export interface ProductVariant {
  id: string;
  sku_id: string;
  color: string | null;
  size: string | null;
  sales_units: number;
  sales_dollars: number;
  avg_selling_price: number | null;
  // Purchases at cost / units received; null without receipts.
  avg_purchase_price: number | null;
  retail_price: number;
  markdown_dollars: number;
  gross_margin_pct: number;
  gmroi: number | null;
  cash_margin_dollars: number;
  cash_margin_pct: number;
  cmroi: number | null;
  inventory_oh_units: number;
  inventory_oh_dollars: number;
  weeks_of_supply: number | null;
  avg_weekly_units: number | null;
  avg_weekly_units_full_price: number | null;
  on_order_units: number;
  on_order_retail: number;
  // Null when an open PO line has no buy price.
  on_order_cost: number | null;
  // On Order $ at the selected Retail / Cost basis -- see withPriceBasis().
  on_order_dollars: number | null;
  // Earliest dated open PO line (yyyy-MM-dd).
  next_arrival_date: string | null;
  // Receipts across every location at retail / at cost, the inputs of imu_pct.
  imu_retail: number;
  imu_cost: number;
  // 0-100 scale; null without receipts.
  imu_pct: number | null;
  purchases_cost_dollars: number;
  inflow_units?: number;
  // Receipts in the period at retail.
  inflow_dollars: number;
  // 0-100 scale; null without receipts.
  mmu_pct: number | null;
  // 0-100 scale; null without sales.
  markdown_pct: number | null;
  cost_of_purchase_pct: number | null;
  sell_through_pct?: number | null;
  sell_through_start_inv_pct?: number | null;
  // Computed from the Weeks of Supply target -- see applySuggestedReorder().
  suggested_reorder: number | null;
}

export interface TopSellerItem {
  id: string;
  sku_id: string;
  product_title: string;
  vendor: string;
  className: string;
  classCode: string | null;
  // Store-level class under className (the plan class); null when the row has none.
  subClassName: string | null;
  category: string;
  locationCode: string | null;
  channel: string;
  sales_dollars: number;
  sales_units: number;
  avg_selling_price: number | null;
  // Purchases at cost / units received; null without receipts.
  avg_purchase_price: number | null;
  retail_price: number;
  cost_price: number;
  // SKU-grain views only -- always null in the By Product rollup (not meaningful across a
  // product's variants). Parsed from POS variant_title (Shopify, Lightspeed X, etc.), so reliability depends on each
  // merchant's own variant-option ordering -- see GetTopSellersReportData.sql's header.
  color: string | null;
  size: string | null;
  markdown_dollars: number;
  gross_margin_pct: number;
  // null means "can't be computed" (no on-hand cost-value data for this SKU in the latest
  // inventory snapshot) -- distinct from a real, confirmed 0.0x GMROI. See renderGMROIBadge().
  gmroi: number | null;
  cash_margin_dollars: number;
  cash_margin_pct: number;
  // Same null-means-"can't compute" convention as gmroi.
  cmroi: number | null;
  inventory_oh_dollars: number;
  inventory_oh_units: number;
  weeks_of_supply: number | null;
  avg_weekly_units: number | null;
  avg_weekly_units_full_price: number | null;
  on_order_units: number;
  on_order_retail: number;
  // Null when an open PO line has no buy price.
  on_order_cost: number | null;
  // On Order $ at the selected Retail / Cost basis -- see withPriceBasis().
  on_order_dollars: number | null;
  // Earliest dated open PO line (yyyy-MM-dd).
  next_arrival_date: string | null;
  // Receipts across every location at retail / at cost, the inputs of imu_pct.
  imu_retail: number;
  imu_cost: number;
  // 0-100 scale; null without receipts.
  imu_pct: number | null;
  purchases_cost_dollars: number;
  inflow_units?: number;
  // Receipts in the period at retail.
  inflow_dollars: number;
  // 0-100 scale; null without receipts.
  mmu_pct: number | null;
  // 0-100 scale; null without sales.
  markdown_pct: number | null;
  cost_of_purchase_pct: number | null;
  sell_through_pct?: number | null;
  sell_through_start_inv_pct?: number | null;
  // Computed from the Weeks of Supply target -- see applySuggestedReorder().
  suggested_reorder: number | null;
  product_tags: string[];
  item_status: string;
  variants: ProductVariant[];

  // YOY Prior Year metrics
  sales_dollars_ly?: number;
  sales_units_ly?: number;
  avg_selling_price_ly?: number | null;
  avg_purchase_price_ly?: number | null;
  markdown_dollars_ly?: number;
  gross_margin_pct_ly?: number | null;
  gmroi_ly?: number | null;
  inventory_oh_units_ly?: number | null;
  inventory_oh_dollars_ly?: number | null;
  markdown_pct_ly?: number | null;
  cost_of_purchase_pct_ly?: number | null;
  mmu_pct_ly?: number | null;
  inflow_units_ly?: number;
  sell_through_pct_ly?: number | null;
  sell_through_start_inv_pct_ly?: number | null;
}

export type TopSellersColumnKey = Exclude<SortableMetricKey, 'product_title'>;

export interface ColumnToggleConfig {
  key: TopSellersColumnKey;
  label: string;
  description: string;
  defaultVisible: boolean;
  align: 'left' | 'right' | 'center';
  padX: 'px-3' | 'px-2.5';
  skuLevelOnly?: boolean;
}

// GMROI / CMROI annualization: the server multiplies by WEEKS_PER_YEAR / period weeks
// (TopSellersService::WEEKS_PER_YEAR); the tooltips name the same factor.
export const WEEKS_PER_YEAR = 52;

export const MMU_DESCRIPTION =
  'Maintained markup: IMU − Markdown % × (1 − IMU), where IMU is the markup on units received in the period ((retail − cost) / retail). Blank without receipts in the period';

export const ON_ORDER_UNITS_DESCRIPTION =
  'Units on open POs within the On-order & OTB span, with the earliest PO delivery date. "Past due" once that date has passed; "—" when no open PO line has a date';
export const ON_ORDER_DOLLARS_DESCRIPTION =
  "Open PO units × each PO line's retail price, or its buy price under Cost. Blank at cost when a PO line has no buy price";
export const IMU_DESCRIPTION =
  'Initial markup before markdowns: (receipts at retail − receipts at cost) / receipts at retail, from units received in the period across all locations, whatever the Location filter. Blank without receipts';

// Single source for every column after Product; list order is the default column order.
export const COLUMN_TOGGLE_LIST: ColumnToggleConfig[] = [
  { key: 'color', label: 'Color', description: 'Variant color, parsed from the POS variant title -- SKU-level views only', defaultVisible: false, align: 'left', padX: 'px-2.5', skuLevelOnly: true },
  { key: 'size', label: 'Size', description: 'Variant size, parsed from the POS variant title -- SKU-level views only', defaultVisible: false, align: 'left', padX: 'px-2.5', skuLevelOnly: true },
  { key: 'suggested_reorder', label: 'Suggested Reorder', description: 'Units to order to reach the Weeks of Supply target: avg weekly full-price units x target weeks - on hand - on order. Items with no full-price sales use all their sales', defaultVisible: true, align: 'right', padX: 'px-3' },
  { key: 'sales_units', label: 'Sales Units', description: 'Units sold in the period', defaultVisible: true, align: 'right', padX: 'px-3' },
  { key: 'sales_dollars', label: 'Sales $', description: 'Net sales dollars', defaultVisible: true, align: 'right', padX: 'px-3' },
  { key: 'inventory_oh_units', label: 'Inventory OH Units', description: 'Current units on-hand', defaultVisible: true, align: 'right', padX: 'px-3' },
  { key: 'on_order_units', label: 'On Order', description: ON_ORDER_UNITS_DESCRIPTION, defaultVisible: true, align: 'right', padX: 'px-3' },
  { key: 'inventory_oh_dollars', label: 'Inventory OH $', description: 'Current on-hand value at retail', defaultVisible: false, align: 'right', padX: 'px-3' },
  { key: 'on_order_dollars', label: 'On Order $', description: ON_ORDER_DOLLARS_DESCRIPTION, defaultVisible: false, align: 'right', padX: 'px-3' },
  { key: 'weeks_of_supply', label: 'Weeks of Supply', description: 'On-hand units / average weekly units sold in the period', defaultVisible: true, align: 'right', padX: 'px-3' },
  { key: 'sell_through_pct', label: 'Sell-Through %', description: 'Units sold / inflow units. Displays "—" when there is no inflow', defaultVisible: true, align: 'right', padX: 'px-3' },
  { key: 'sell_through_start_inv_pct', label: 'Sell-Through (w/ Start Inv) %', description: 'Units sold / (units sold + on-hand units)', defaultVisible: false, align: 'right', padX: 'px-3' },
  { key: 'vendor', label: 'Vendor', description: 'Vendor supplying this item', defaultVisible: true, align: 'left', padX: 'px-3' },
  { key: 'className', label: 'Class', description: 'Merchandise class or department', defaultVisible: true, align: 'left', padX: 'px-2.5' },
  { key: 'classCode', label: 'Class #', description: 'Merchandise class code number', defaultVisible: false, align: 'left', padX: 'px-2.5' },
  { key: 'subClassName', label: SUB_CLASS_LABEL, description: SUB_CLASS_DESCRIPTION, defaultVisible: false, align: 'left', padX: 'px-2.5' },
  { key: 'avg_selling_price', label: AVG_SELLING_PRICE_LABEL, description: AVG_SELLING_PRICE_DESCRIPTION, defaultVisible: false, align: 'right', padX: 'px-3' },
  { key: 'retail_price', label: 'Retail Price', description: 'Full ticketed price', defaultVisible: false, align: 'right', padX: 'px-3' },
  { key: 'markdown_dollars', label: 'Markdown $', description: 'Total discount dollars taken on this item', defaultVisible: false, align: 'right', padX: 'px-3' },
  { key: 'markdown_pct', label: 'Markdown %', description: 'Markdown $ as a percentage of net sales', defaultVisible: false, align: 'right', padX: 'px-3' },
  { key: 'gross_margin_pct', label: 'Initial Margin %', description: 'Margin before markdowns, on gross (ticketed) sales: (Gross sales − COGS) / Gross sales', defaultVisible: true, align: 'right', padX: 'px-3' },
  { key: 'mmu_pct', label: 'MMU %', description: MMU_DESCRIPTION, defaultVisible: false, align: 'right', padX: 'px-3' },
  { key: 'imu_pct', label: 'IMU %', description: IMU_DESCRIPTION, defaultVisible: false, align: 'right', padX: 'px-3' },
  { key: 'gmroi', label: 'GMROI', description: `Gross margin return on inventory investment, annualized: (Gross sales − COGS) / current on-hand cost × ${WEEKS_PER_YEAR} / weeks in the period`, defaultVisible: false, align: 'center', padX: 'px-3' },
  { key: 'cash_margin_dollars', label: 'Cash Margin $', description: 'Net sales − purchases at cost. Purchases at cost = units received in the period x the actual unit cost of each SKU', defaultVisible: false, align: 'right', padX: 'px-3' },
  { key: 'cash_margin_pct', label: 'Cash Margin %', description: 'Cash Margin $ / net sales, i.e. 100% − Cost of Purchase %', defaultVisible: false, align: 'right', padX: 'px-3' },
  { key: 'cmroi', label: 'CMROI', description: `Cash margin return on inventory investment, annualized: Cash Margin $ / current on-hand cost × ${WEEKS_PER_YEAR} / weeks in the period`, defaultVisible: false, align: 'right', padX: 'px-3' },
  { key: 'cost_of_purchase_pct', label: 'Cost of Purchase %', description: 'Purchases at cost / net sales. Purchases at cost = units received in the period x the actual unit cost of each SKU', defaultVisible: false, align: 'right', padX: 'px-3' },
  { key: 'avg_purchase_price', label: AVG_PURCHASE_PRICE_LABEL, description: AVG_PURCHASE_PRICE_DESCRIPTION, defaultVisible: false, align: 'right', padX: 'px-3' },
];

export const WEEKS_OF_SUPPLY_OPTIONS = [4, 8, 12, 16, 26] as const;
export const DEFAULT_WEEKS_OF_SUPPLY_TARGET = 8;

// Suggested Reorder covers either a number of weeks or up to a date (e.g. a product's out-date).
export type ReorderTargetMode = 'weeks' | 'date';

const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Weeks from `today` to a cover-through date (yyyy-MM-dd): (date - today) / 7, counting whole
 * calendar days so the time of day and DST can't shift it. Null for an invalid date, today or a
 * past date -- callers fall back to the Weeks of Supply target.
 */
export function weeksUntilCoverDate(isoDate: string, today: Date = new Date()): number | null {
  const match = ISO_DATE_PATTERN.exec(isoDate);
  if (!match) return null;
  const [year, month, day] = [Number(match[1]), Number(match[2]) - 1, Number(match[3])];
  const target = new Date(Date.UTC(year, month, day));
  // Date.UTC rolls 2026-02-30 over to March; a real calendar date round-trips unchanged.
  if (target.getUTCFullYear() !== year || target.getUTCMonth() !== month || target.getUTCDate() !== day) return null;
  const days = Math.round((target.getTime() - Date.UTC(today.getFullYear(), today.getMonth(), today.getDate())) / MS_PER_DAY);
  return days > 0 ? days / 7 : null;
}

/**
 * Units to order to cover `targetWeeks` at the current weekly sales rate, net of on-hand and
 * on-order. 0 when already covered; null when there's no sales rate to project from.
 */
export function computeSuggestedReorderUnits(
  avgWeeklyUnits: number | null,
  onHandUnits: number,
  onOrderUnits: number,
  targetWeeks: number
): number | null {
  if (avgWeeklyUnits === null || avgWeeklyUnits === undefined || avgWeeklyUnits <= 0) return null;
  // Tolerance keeps float noise (e.g. 24.000000001) from rounding up a whole unit.
  const needed = Math.ceil(avgWeeklyUnits * targetWeeks - Math.max(onHandUnits, 0) - Math.max(onOrderUnits, 0) - 1e-9);
  return Math.max(needed, 0);
}

/**
 * The weekly rate Suggested Reorder projects: full-price units, so promotional demand isn't
 * reordered. An item with no positive full-price rate (it sold only on promo) uses all its sales.
 */
export const reorderWeeklyUnits = (
  avgWeeklyUnitsFullPrice: number | null | undefined,
  avgWeeklyUnits: number | null
): number | null =>
  avgWeeklyUnitsFullPrice !== null && avgWeeklyUnitsFullPrice !== undefined && avgWeeklyUnitsFullPrice > 0
    ? avgWeeklyUnitsFullPrice
    : avgWeeklyUnits;

const sumNullable = (values: (number | null)[]): number | null =>
  values.some((v) => v !== null) ? values.reduce<number>((sum, v) => sum + (v ?? 0), 0) : null;

/**
 * Computes Suggested Reorder for an item and its variants. A product with variants (By SKU) sums
 * its variants' suggestions, so a stocked-out size isn't hidden by an overstocked one.
 */
export function applySuggestedReorder<T extends TopSellerItem>(item: T, targetWeeks: number): T {
  const variants = item.variants.map((v) => ({
    ...v,
    suggested_reorder: computeSuggestedReorderUnits(
      reorderWeeklyUnits(v.avg_weekly_units_full_price, v.avg_weekly_units),
      v.inventory_oh_units,
      v.on_order_units,
      targetWeeks
    ),
  }));
  const suggested = variants.length > 0
    ? sumNullable(variants.map((v) => v.suggested_reorder))
    : computeSuggestedReorderUnits(
      reorderWeeklyUnits(item.avg_weekly_units_full_price, item.avg_weekly_units),
      item.inventory_oh_units,
      item.on_order_units,
      targetWeeks
    );
  return { ...item, variants, suggested_reorder: suggested };
}

export const formatReorderUnits = (units: number | null | undefined): string =>
  units === null || units === undefined || units <= 0 ? '—' : units.toLocaleString();

export type PriceBasis = 'retail' | 'cost';

// "Nov 15" (with the year outside the current one), "Past due" before today, "—" without a date.
export const formatNextArrival = (isoDate: string | null | undefined, today: Date = new Date()): string => {
  const match = isoDate ? ISO_DATE_PATTERN.exec(isoDate) : null;
  if (!match) return '—';
  const [year, month, day] = [Number(match[1]), Number(match[2]) - 1, Number(match[3])];
  const arrival = new Date(year, month, day);
  if (arrival < new Date(today.getFullYear(), today.getMonth(), today.getDate())) return 'Past due';
  return arrival.toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...(year !== today.getFullYear() ? { year: 'numeric' } : {}) });
};

// "24 units (Nov 15)"; "—" with nothing on order.
export const formatOnOrderUnits = (units: number, nextArrival: string | null | undefined, today?: Date): string =>
  units > 0 ? `${units.toLocaleString()} ${units === 1 ? 'unit' : 'units'} (${formatNextArrival(nextArrival, today)})` : '—';

// "—" with nothing on order, or when the cost of an open line is unknown.
export const formatOnOrderDollars = (units: number, dollars: number | null | undefined): string =>
  units > 0 && dollars != null ? formatCurrency(dollars) : '—';

// Sets On Order $ from the selected basis on an item and its SKUs, before grouping, so the column,
// group rows, the Total row, sorting and the CSV all read one field.
export function withPriceBasis<T extends TopSellerItem>(item: T, priceBasis: PriceBasis): T {
  const pick = (row: Pick<ProductVariant, 'on_order_retail' | 'on_order_cost'>) =>
    priceBasis === 'cost' ? row.on_order_cost : row.on_order_retail;
  return { ...item, on_order_dollars: pick(item), variants: item.variants.map((v) => ({ ...v, on_order_dollars: pick(v) })) };
}

// A whole target stays whole ("8"); a date-derived one keeps one decimal ("14.3").
export const formatTargetWeeks = (weeks: number): string => (Number.isInteger(weeks) ? String(weeks) : weeks.toFixed(1));

// Suggested Reorder's header names the target it covers (SPS-5643 #5), e.g. "Suggested Reorder (target 8 wks)".
// On Order $ names its price basis, e.g. "On Order $ (Cost)".
export const columnHeaderLabel = (
  col: Pick<ColumnToggleConfig, 'key' | 'label'>,
  targetWeeks: number,
  priceBasis: PriceBasis = 'retail'
): string => {
  if (col.key === 'suggested_reorder' && targetWeeks > 0) return `${col.label} (target ${formatTargetWeeks(targetWeeks)} wks)`;
  if (col.key === 'on_order_dollars') return `${col.label} (${priceBasis === 'cost' ? 'Cost' : 'Retail'})`;
  return col.label;
};

// Above this Markdown % (0-100), the weekly sales rate behind Suggested Reorder may be promo-driven (SPS-5643 #7).
export const MARKDOWN_PROMO_THRESHOLD_PCT = 20;
export const PROMO_DRIVEN_RATE_TOOLTIP = `Sales rate may be promo-driven (markdown > ${MARKDOWN_PROMO_THRESHOLD_PCT}%)`;

export const isPromoDrivenRate = (markdownPct: number | null | undefined): boolean =>
  markdownPct !== null && markdownPct !== undefined && markdownPct > MARKDOWN_PROMO_THRESHOLD_PCT;

export const formatWeeks = (weeks: number | null | undefined): string =>
  weeks === null || weeks === undefined ? '—' : weeks.toFixed(1);

// For 0-100 percentages that may be unknown (null).
export const formatOptionalPercent = (val: number | null | undefined): string =>
  val === null || val === undefined ? '—' : `${val.toFixed(1)}%`;

const toPercentOrNull = (fraction: number | null | undefined): number | null =>
  fraction === null || fraction === undefined ? null : fraction * 100;

/**
 * MMU % on a 0-100 scale, as TopSellersService::mmuPct() computes it: IMU - Markdown % x (1 - IMU),
 * with IMU = (receipts at retail - purchases at cost) / receipts at retail and Markdown % = markdown $
 * / net sales (0 without sales). Null without receipts at retail.
 */
export const computeMmuPct = (
  inflowRetailDollars: number,
  purchasesCostDollars: number,
  markdownDollars: number,
  salesDollars: number
): number | null => {
  if (!(inflowRetailDollars > 0)) return null;
  const imu = (inflowRetailDollars - purchasesCostDollars) / inflowRetailDollars;
  const markdownRate = salesDollars > 0 ? markdownDollars / salesDollars : 0;
  return (imu - markdownRate * (1 - imu)) * 100;
};

export function computeSellThroughPct(
  salesUnits: number,
  inflowUnits: number,
  rawFraction?: number | null
): number | null {
  if (rawFraction !== undefined && rawFraction !== null && !isNaN(rawFraction)) {
    return rawFraction * 100;
  }
  return inflowUnits > 0 ? (salesUnits / inflowUnits) * 100 : null;
}

export function computeSellThroughStartInvPct(
  salesUnits: number,
  ohUnits: number,
  rawFraction?: number | null
): number | null {
  if (rawFraction !== undefined && rawFraction !== null && !isNaN(rawFraction)) {
    return rawFraction * 100;
  }
  const total = salesUnits + ohUnits;
  return total > 0 ? (salesUnits / total) * 100 : null;
}

export const DEFAULT_COLUMN_ORDER: TopSellersColumnKey[] = COLUMN_TOGGLE_LIST.map((col) => col.key);

export const METRIC_COLUMN_WIDTH = 140;
export const PRODUCT_COLUMN_KEY = 'product_title';
export const PRODUCT_COLUMN_DESCRIPTION =
  'Ranked by the column you sort by -- pick a sort from any column header to change the ranking';

// Columns whose header menu sorts A to Z / Z to A instead of low to high / high to low.
export const TEXT_SORT_KEYS: readonly SortableMetricKey[] = ['product_title', 'vendor', 'className', 'classCode', 'subClassName', 'color', 'size'];
const DEFAULT_COLUMN_WIDTHS: Readonly<Record<string, number>> = { [PRODUCT_COLUMN_KEY]: 300 };
// Category / Class by Product stack two plan pills per line under each group name, which needs a
// wider first column; a width the user drags still overrides this.
const OTB_GROUP_COLUMN_WIDTHS: Readonly<Record<string, number>> = { [PRODUCT_COLUMN_KEY]: 340 };
export const getTopSellersDefaultColumnWidths = (hierarchy: TopSellersHierarchy): Readonly<Record<string, number>> =>
  hierarchy === 'category' || hierarchy === 'class' ? OTB_GROUP_COLUMN_WIDTHS : DEFAULT_COLUMN_WIDTHS;

export const filterZeroSalesVariants = (variants: ProductVariant[], hideZeroSales: boolean): ProductVariant[] =>
  hideZeroSales ? variants.filter((variant) => variant.sales_units > 0) : variants;

export const isColumnAvailableInView = (col: ColumnToggleConfig, view: TopSellersView): boolean =>
  !(col.skuLevelOnly && view === 'by_product');

// SKU-level columns default on in By SKU only, until the user picks a visibility for them.
export const isColumnVisibleInView = (
  col: ColumnToggleConfig,
  view: TopSellersView,
  visibleColumns: Record<string, boolean>
): boolean =>
  isColumnAvailableInView(col, view)
  && (visibleColumns[col.key] ?? (col.skuLevelOnly ? view === 'by_sku' : col.defaultVisible));

const COLUMN_CONFIG_BY_KEY = new Map<TopSellersColumnKey, ColumnToggleConfig>(
  COLUMN_TOGGLE_LIST.map((col) => [col.key, col])
);

export const CUSTOM_DATE_RANGE = 'Custom range...';

/**
 * Resolves the "Date range" dropdown's label to a concrete [from, to] pair.
 * When "Custom range..." is selected, customStart and customEnd are used.
 */
export const TOP_SELLERS_DATE_RANGE_PRESETS = [
  '1 Month',
  '3 Months',
  '6 Months',
  'Month to date',
  'Quarter to date',
  'Year to date',
  CUSTOM_DATE_RANGE,
] as const;

export const DEFAULT_TOP_SELLERS_DATE_RANGE = '3 Months';
export const ALL_CHANNELS = 'All channels';
export const NO_MIN_SALES = 'None';

export function normalizeDateRangePreset(val: string): string {
  const lower = val.trim().toLowerCase();
  if (lower === 'trailing 30 days' || lower === '1 month' || lower === 'last 1 month') return '1 Month';
  if (lower === 'trailing 90 days' || lower === '3 months' || lower === 'last 3 months') return '3 Months';
  if (lower === 'trailing 180 days' || lower === '6 months' || lower === 'last 6 months') return '6 Months';
  return val;
}

/**
 * Resolves the "Date range" dropdown's label to a concrete [from, to] pair.
 * When "Custom range..." is selected, customStart and customEnd are used.
 */
// A local calendar date (yyyy-MM-dd), formatted as Vendor Performance formats it. Never
// toISOString(): its UTC date runs a day ahead in a US evening.
export const toLocalIsoDate = (d: Date): string => formatDate(d, 'yyyy-MM-dd');

// The last complete day of sales. Every preset, the default custom range and the date pickers end
// here -- today is still a partial day.
export const lastCompleteIsoDate = (today: Date = new Date()): string => toLocalIsoDate(addDays(today, -1));

// Top Sellers preset (lower-cased, after normalizeDateRangePreset) => the Vendor Performance preset
// it means. Both reports then resolve identical dates from one resolver.
const VENDOR_PERFORMANCE_PRESETS: Record<string, string> = {
  '1 month': 'Last 1 Month',
  '3 months': 'Last 3 Months',
  '6 months': 'Last 6 Months',
  'month to date': 'Month to date',
  'year to date': 'Year to date',
};
const VENDOR_PERFORMANCE_DEFAULT_PRESET = VENDOR_PERFORMANCE_PRESETS[DEFAULT_TOP_SELLERS_DATE_RANGE.toLowerCase()];
const MONTHS_PER_QUARTER = 3;

export function resolveDateRange(label: string, customStart?: string, customEnd?: string, today: Date = new Date()): { from: string; to: string } {
  const normalized = normalizeDateRangePreset(label).trim().toLowerCase();
  const sharedPreset = VENDOR_PERFORMANCE_PRESETS[normalized];
  if (sharedPreset) {
    return resolveVendorPerformanceDateRange(sharedPreset, undefined, today);
  }

  const to = lastCompleteIsoDate(today);

  // Top Sellers only; runs through yesterday like the rest, from yesterday's quarter.
  if (normalized === 'quarter to date') {
    const lastCompleteDay = addDays(today, -1);
    const quarterStartMonth = Math.floor(lastCompleteDay.getMonth() / MONTHS_PER_QUARTER) * MONTHS_PER_QUARTER;
    return { from: toLocalIsoDate(new Date(lastCompleteDay.getFullYear(), quarterStartMonth, 1)), to };
  }
  if (normalized.startsWith('custom range')) {
    const resolvedEnd = customEnd && customEnd <= to ? customEnd : to;
    return {
      from: customStart || resolveVendorPerformanceDateRange(VENDOR_PERFORMANCE_DEFAULT_PRESET, undefined, today).from,
      to: resolvedEnd,
    };
  }

  return resolveVendorPerformanceDateRange(VENDOR_PERFORMANCE_DEFAULT_PRESET, undefined, today);
}

// Item identity: variant_id at SKU grain; product rows carry their product_id in sku_id, plus the
// store under Product by Location, where one product repeats per store.
export const rowIdentity = (row: { variant_id?: string | null; sku_id: string | null; location_code?: string | null }): string => {
  const base = row.variant_id || row.sku_id || '';
  return row.location_code ? `${base}@${row.location_code}` : base;
};

export const formatSkuLabel = (sku: string | null | undefined): string =>
  sku && sku.trim() !== '' ? sku : 'No SKU';

// Class filter button text for one selected class: "[100] Accessories", as in Vendor Performance.
// Just the name when the code is missing or equal to it; the raw code if the option isn't loaded.
export const selectedClassLabel = (options: DropdownOption[], code: string): string => {
  const found = options.find((o) => o.code === code);
  if (!found) return code;
  return found.code && found.code !== found.name ? `[${found.code}] ${found.name}` : found.name;
};

// Class filter search: matches the class name or its code.
export const matchesClassQuery = (option: DropdownOption, query: string): boolean => {
  const q = query.trim().toLowerCase();
  return option.name.toLowerCase().includes(q) || (!!option.code && option.code.toLowerCase().includes(q));
};

// Values a variant option can take when it is a size rather than a color -- same patterns as
// TopSellersService::SIZE_PATTERNS, matched against the whole trimmed value.
const SIZE_PATTERNS: readonly RegExp[] = [
  /^\d{1,3}(\.\d{1,2})?( ?1\/2)? ?(W|M|N|WW|XW|T|Y)?$/i, // 9, 9.5, 10W, 8.5M, 7 1/2, 2T
  /^\d{2} ?x ?\d{2}$/i, // 32x30
  /^(X{0,3}S|S|M|L|X{1,4}L|[2-6]XL|[1-6]X)([/-](X{0,3}S|S|M|L|X{1,4}L|[2-6]XL|[1-6]X))?$/i, // XS-XXXL, 2XL, 1X, S/M
  /^((X{1,3}|EXTRA)[ -]?)?(SMALL|MEDIUM|LARGE)$/i, // Small, X-Large, Extra Large
  /^(OS|O\/S|OSFA|OSFM|ONE[ -]?SIZE)$/i, // One size
];

export const isLikelySize = (value: string | null | undefined): boolean => {
  const trimmed = (value ?? '').trim();
  return trimmed !== '' && SIZE_PATTERNS.some((pattern) => pattern.test(trimmed));
};

/**
 * Color and size are split from POS variant_title (Shopify, Lightspeed X, etc.) by position, so a size-only product
 * ("9") or size-first options ("9 / Black") put the size under Color. Mirrors
 * TopSellersService::normalizeVariantOptions() (the API already applies it; this guards older
 * responses): sizes move to `size`, "Default Title" and blanks become null, and two sizes (waist
 * / inseam) stay together as the size.
 */
export const normalizeVariantColorSize = (
  color: string | null | undefined,
  size: string | null | undefined
): { color: string | null; size: string | null } => {
  const clean = (value: string | null | undefined): string | null => {
    const trimmed = (value ?? '').trim();
    return trimmed === '' || trimmed.toLowerCase() === 'default title' ? null : trimmed;
  };
  const c = clean(color);
  const s = clean(size);
  if (c === null || !isLikelySize(c)) return { color: c, size: s };
  if (s === null) return { color: null, size: c };
  if (isLikelySize(s)) return { color: null, size: `${c} / ${s}` };
  return { color: s, size: c };
};

/**
 * Shapes one nested SkuVariantApiRow (By SKU view only) into this component's ProductVariant
 * shape -- same 0-1 -> 0-100 percent conversion and null-passthrough conventions as
 * mapApiRowToItem() below, since variants share the same metric fields as a normal item row.
 */
export function mapApiVariantToProductVariant(row: SkuVariantApiRow): ProductVariant {
  return {
    id: rowIdentity(row),
    sku_id: row.sku_id ?? '',
    ...normalizeVariantColorSize(row.color, row.size),
    sales_units: row.sales_units,
    sales_dollars: row.sales_dollars,
    avg_selling_price: avgPerUnit(row.sales_dollars, row.sales_units),
    avg_purchase_price: avgPerUnit(row.purchases_cost_dollars, row.inflow_units),
    retail_price: row.retail_price ?? 0,
    markdown_dollars: row.markdown_dollars,
    gross_margin_pct: (row.gross_margin_pct ?? 0) * 100,
    gmroi: row.gmroi,
    cash_margin_dollars: row.cash_margin_dollars ?? 0,
    cash_margin_pct: (row.cash_margin_pct ?? 0) * 100,
    cmroi: row.cmroi,
    inventory_oh_units: row.inventory_oh_units,
    inventory_oh_dollars: row.inventory_oh_dollars,
    weeks_of_supply: row.weeks_of_supply ?? null,
    avg_weekly_units: row.avg_weekly_units ?? null,
    avg_weekly_units_full_price: row.avg_weekly_units_full_price ?? null,
    on_order_units: row.on_order_units ?? 0,
    on_order_retail: row.on_order_retail ?? 0,
    on_order_cost: row.on_order_cost ?? null,
    on_order_dollars: row.on_order_retail ?? 0,
    next_arrival_date: row.next_arrival_date ?? null,
    imu_retail: row.imu_retail ?? 0,
    imu_cost: row.imu_cost ?? 0,
    imu_pct: toPercentOrNull(row.imu_pct),
    purchases_cost_dollars: row.purchases_cost_dollars ?? 0,
    inflow_units: row.inflow_units ?? 0,
    inflow_dollars: row.inflow_dollars ?? 0,
    mmu_pct: toPercentOrNull(row.mmu_pct),
    markdown_pct: toPercentOrNull(row.markdown_pct),
    cost_of_purchase_pct: toPercentOrNull(row.cost_of_purchase_pct),
    sell_through_pct: computeSellThroughPct(
      row.sales_units,
      row.inflow_units ?? 0,
      row.sell_through_pct
    ),
    sell_through_start_inv_pct: computeSellThroughStartInvPct(
      row.sales_units,
      row.inventory_oh_units,
      row.sell_through_start_inv_pct
    ),
    suggested_reorder: null,
  };
}

/**
 * Shapes one GET /api/reports/top-sellers row into this component's TopSellerItem shape.
 * `channel`/`cost_price`/`product_tags`/`item_status` have no backend source yet, so they're
 * defaulted rather than fabricated. `gross_margin_pct`/`cash_margin_pct` are converted from the
 * backend's 0-1 fraction to the 0-100 scale formatPercent() expects. `gmroi`/`cmroi` are passed
 * through as-is (including `null`) -- see renderGMROIBadge()'s null branch for why they aren't
 * defaulted to 0. `color`/`size` are passed through as-is too (null outside the SKU-grain views).
 * `variants` is only ever non-empty in the By SKU view.
 */
function mapApiRowToItem(row: TopSellerApiRow, includeLy: boolean): TopSellerItem & { liveRank: number } {
  return {
    id: rowIdentity(row),
    sku_id: row.sku_id ?? '',
    product_title: row.product_title,
    vendor: row.vendor,
    className: row.m1_class_name,
    classCode: row.m1_class_code,
    subClassName: row.leaf_class_name ?? null,
    category: row.category,
    locationCode: row.location_code ?? null,
    channel: '',
    sales_dollars: row.sales_dollars,
    sales_units: row.sales_units,
    avg_selling_price: avgPerUnit(row.sales_dollars, row.sales_units),
    avg_purchase_price: avgPerUnit(row.purchases_cost_dollars, row.inflow_units),
    retail_price: row.retail_price ?? 0,
    cost_price: 0,
    ...normalizeVariantColorSize(row.color, row.size),
    markdown_dollars: row.markdown_dollars,
    gross_margin_pct: (row.gross_margin_pct ?? 0) * 100,
    gmroi: row.gmroi,
    cash_margin_dollars: row.cash_margin_dollars ?? 0,
    // Same 0-1 -> 0-100 conversion as gross_margin_pct.
    cash_margin_pct: (row.cash_margin_pct ?? 0) * 100,
    cmroi: row.cmroi,
    inventory_oh_dollars: row.inventory_oh_dollars,
    inventory_oh_units: row.inventory_oh_units,
    weeks_of_supply: row.weeks_of_supply ?? null,
    avg_weekly_units: row.avg_weekly_units ?? null,
    avg_weekly_units_full_price: row.avg_weekly_units_full_price ?? null,
    on_order_units: row.on_order_units ?? 0,
    on_order_retail: row.on_order_retail ?? 0,
    on_order_cost: row.on_order_cost ?? null,
    on_order_dollars: row.on_order_retail ?? 0,
    next_arrival_date: row.next_arrival_date ?? null,
    imu_retail: row.imu_retail ?? 0,
    imu_cost: row.imu_cost ?? 0,
    imu_pct: toPercentOrNull(row.imu_pct),
    purchases_cost_dollars: row.purchases_cost_dollars ?? 0,
    inflow_units: row.inflow_units ?? 0,
    inflow_dollars: row.inflow_dollars ?? 0,
    mmu_pct: toPercentOrNull(row.mmu_pct),
    markdown_pct: toPercentOrNull(row.markdown_pct),
    cost_of_purchase_pct: toPercentOrNull(row.cost_of_purchase_pct),
    sell_through_pct: computeSellThroughPct(
      row.sales_units,
      row.inflow_units ?? 0,
      row.sell_through_pct
    ),
    sell_through_start_inv_pct: computeSellThroughStartInvPct(
      row.sales_units,
      row.inventory_oh_units,
      row.sell_through_start_inv_pct
    ),
    suggested_reorder: null,
    product_tags: [],
    item_status: '',
    variants: (row.variants ?? []).map(mapApiVariantToProductVariant),
    sales_dollars_ly: includeLy ? row.sales_dollars_ly : undefined,
    sales_units_ly: includeLy ? row.sales_units_ly : undefined,
    avg_selling_price_ly: includeLy ? avgPerUnit(row.sales_dollars_ly, row.sales_units_ly) : undefined,
    avg_purchase_price_ly: includeLy ? avgPerUnit(row.purchases_cost_dollars_ly, row.inflow_units_ly) : undefined,
    markdown_dollars_ly: includeLy ? row.markdown_dollars_ly : undefined,
    // Same 0-1 -> 0-100 conversion as the This Period value above.
    gross_margin_pct_ly: includeLy
      ? (row.gross_margin_pct_ly === null || row.gross_margin_pct_ly === undefined ? null : row.gross_margin_pct_ly * 100)
      : undefined,
    gmroi_ly: includeLy ? (row.gmroi_ly ?? null) : undefined,
    inventory_oh_units_ly: includeLy ? row.inventory_oh_units_ly : undefined,
    inventory_oh_dollars_ly: includeLy ? row.inventory_oh_dollars_ly : undefined,
    markdown_pct_ly: includeLy ? toPercentOrNull(row.markdown_pct_ly) : undefined,
    cost_of_purchase_pct_ly: includeLy ? toPercentOrNull(row.cost_of_purchase_pct_ly) : undefined,
    mmu_pct_ly: includeLy ? toPercentOrNull(row.mmu_pct_ly) : undefined,
    inflow_units_ly: includeLy ? (row.inflow_units_ly ?? 0) : undefined,
    sell_through_pct_ly: includeLy
      ? computeSellThroughPct(
        row.sales_units_ly ?? 0,
        row.inflow_units_ly ?? 0,
        row.sell_through_pct_ly
      )
      : undefined,
    sell_through_start_inv_pct_ly: includeLy
      ? (row.inventory_oh_units_ly == null
        ? null
        : computeSellThroughStartInvPct(
          row.sales_units_ly ?? 0,
          row.inventory_oh_units_ly,
          row.sell_through_start_inv_pct_ly
        ))
      : undefined,
    liveRank: row.item_rank,
  };
}

// The filter bar as a saved view restores it. Custom dates are kept only for a custom range;
// presets re-resolve against today when the view is applied.
export interface TopSellersViewFilters {
  dateRange: string;
  customStartDate?: string;
  customEndDate?: string;
  selectedLocations: string[];
  excludeLocation: boolean;
  selectedClasses: string[];
  excludeClass: boolean;
  selectedCategories: string[];
  excludeCategory: boolean;
  selectedVendors: string[];
  excludeVendor: boolean;
  selectedChannels: string;
  minSales: string;
  yoyComparison: boolean;
  showPctChange: boolean;
  hideZeroSalesSkus: boolean;
}

export const DEFAULT_TOP_SELLERS_FILTERS: TopSellersViewFilters = {
  dateRange: DEFAULT_TOP_SELLERS_DATE_RANGE,
  selectedLocations: [],
  excludeLocation: false,
  selectedClasses: [],
  excludeClass: false,
  selectedCategories: [],
  excludeCategory: false,
  selectedVendors: [],
  excludeVendor: false,
  selectedChannels: ALL_CHANNELS,
  minSales: NO_MIN_SALES,
  yoyComparison: false,
  showPctChange: false,
  hideZeroSalesSkus: false,
};

export interface SavedViewItem {
  id: string;
  name: string;
  view: TopSellersView;
  hierarchy?: TopSellersHierarchy;
  rankBy: RankByMetric;
  topN: number;
  visibleColumns: string[];
  isDefault?: boolean;
  columnOrder?: TopSellersColumnKey[];
  weeksOfSupplyTarget?: number;
  // Set when the view was saved in cover-through-date mode.
  coverThroughDate?: string;
  onOrderSpan?: string;
  onOrderStartDate?: string;
  onOrderEndDate?: string;
  // Absent on views saved before the toggle existed, which open at retail.
  priceBasis?: PriceBasis;
  // Absent on built-in presets and older views, which leave the current filters as they are.
  filters?: TopSellersViewFilters;
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const codeList = (value: unknown, fallback: string[]): string[] =>
  Array.isArray(value) ? value.filter((v) => typeof v === 'string' || typeof v === 'number').map(String) : fallback;

// The dropdown's own label for a stored date range (older labels included), or undefined if unknown.
const matchDateRangePreset = (label: string): string | undefined => {
  const normalized = normalizeDateRangePreset(label).trim().toLowerCase();
  return TOP_SELLERS_DATE_RANGE_PRESETS.find((preset) => preset.toLowerCase() === normalized);
};

// A state updater keeping the current list when the codes match, so an unchanged filter doesn't refetch.
const keepIfSameCodes = (next: string[]) => (prev: string[]): string[] =>
  prev.length === next.length && prev.every((code) => next.includes(code)) ? prev : next;

/**
 * A stored view's filters with each field checked, defaulting any that are missing or malformed.
 * Null when the view carries no filters.
 */
export const resolveSavedViewFilters = (raw: unknown): TopSellersViewFilters | null => {
  if (!isPlainObject(raw)) return null;
  const defaults = DEFAULT_TOP_SELLERS_FILTERS;
  const text = (key: keyof TopSellersViewFilters, fallback: string): string => {
    const value = raw[key];
    return typeof value === 'string' && value.trim() ? value : fallback;
  };
  const flag = (key: keyof TopSellersViewFilters, fallback: boolean): boolean => {
    const value = raw[key];
    return typeof value === 'boolean' ? value : fallback;
  };
  const isoDate = (key: keyof TopSellersViewFilters): string | undefined => {
    const value = raw[key];
    return typeof value === 'string' && ISO_DATE_PATTERN.test(value) ? value : undefined;
  };

  const dateRange = matchDateRangePreset(text('dateRange', defaults.dateRange)) ?? defaults.dateRange;
  const minSales = typeof raw.minSales === 'number' && Number.isFinite(raw.minSales) ? String(raw.minSales) : text('minSales', defaults.minSales);
  return {
    dateRange,
    ...(dateRange === CUSTOM_DATE_RANGE ? { customStartDate: isoDate('customStartDate'), customEndDate: isoDate('customEndDate') } : {}),
    selectedLocations: codeList(raw.selectedLocations, defaults.selectedLocations),
    excludeLocation: flag('excludeLocation', defaults.excludeLocation),
    selectedClasses: codeList(raw.selectedClasses, defaults.selectedClasses),
    excludeClass: flag('excludeClass', defaults.excludeClass),
    selectedCategories: codeList(raw.selectedCategories, defaults.selectedCategories),
    excludeCategory: flag('excludeCategory', defaults.excludeCategory),
    selectedVendors: codeList(raw.selectedVendors, defaults.selectedVendors),
    excludeVendor: flag('excludeVendor', defaults.excludeVendor),
    selectedChannels: text('selectedChannels', defaults.selectedChannels),
    minSales,
    yoyComparison: flag('yoyComparison', defaults.yoyComparison),
    showPctChange: flag('showPctChange', defaults.showPctChange),
    hideZeroSalesSkus: flag('hideZeroSalesSkus', defaults.hideZeroSalesSkus),
  };
};

// A built-in view's columns, in COLUMN_TOGGLE_LIST order. SKU-level columns (Color/Size) are always
// included: they only show at SKU grain, matching how the default layout treats them.
const columnsFor = (include: (col: ColumnToggleConfig) => boolean): string[] =>
  COLUMN_TOGGLE_LIST.filter((col) => col.skuLevelOnly || include(col)).map((col) => col.key);

const columnsWithKeys = (keys: readonly TopSellersColumnKey[]) => columnsFor((col) => keys.includes(col.key));

export const DEFAULT_VISIBLE_COLUMNS = columnsFor((col) => col.defaultVisible);

// The three "reports" from the 09/29 call (SPS-5643 #6), each a column subset of COLUMN_TOGGLE_LIST.
export const PRESET_VIEW_COLUMNS = {
  reorder: columnsWithKeys([
    'suggested_reorder', 'weeks_of_supply', 'sales_units', 'inventory_oh_units', 'inventory_oh_dollars',
    'sell_through_pct', 'vendor', 'className', 'classCode',
  ]),
  margin_markdown: columnsWithKeys([
    'sales_dollars', 'avg_selling_price', 'retail_price', 'markdown_dollars', 'markdown_pct', 'gross_margin_pct',
    'gmroi', 'cash_margin_dollars', 'cash_margin_pct', 'cmroi', 'vendor', 'className',
  ]),
  sales_recap: columnsWithKeys([
    'sales_units', 'sales_dollars', 'avg_selling_price', 'sell_through_pct', 'sell_through_start_inv_pct',
    'inventory_oh_units', 'vendor', 'className',
  ]),
};

// Built-in views are always listed and never stored; user views persist per user + client.
export const BUILT_IN_SAVED_VIEWS: SavedViewItem[] = [
  {
    id: 'sv-1',
    name: 'Default Top Sales $',
    view: 'by_product',
    hierarchy: 'total',
    rankBy: 'sales_dollars',
    topN: 25,
    visibleColumns: DEFAULT_VISIBLE_COLUMNS,
    isDefault: true,
  },
  {
    id: 'sv-2',
    name: 'By Class Performance',
    view: 'by_product',
    hierarchy: 'class',
    rankBy: 'sales_dollars',
    topN: 25,
    visibleColumns: DEFAULT_VISIBLE_COLUMNS,
  },
  {
    id: 'sv-reorder',
    name: 'Reorder',
    view: 'by_product',
    hierarchy: 'total',
    rankBy: 'sales_dollars',
    topN: 25,
    visibleColumns: PRESET_VIEW_COLUMNS.reorder,
  },
  {
    id: 'sv-margin-markdown',
    name: 'Margin & Markdown',
    view: 'by_product',
    hierarchy: 'total',
    rankBy: 'sales_dollars',
    topN: 25,
    visibleColumns: PRESET_VIEW_COLUMNS.margin_markdown,
  },
  {
    id: 'sv-sales-recap',
    name: 'Sales Recap',
    view: 'by_product',
    hierarchy: 'total',
    rankBy: 'sales_dollars',
    topN: 25,
    visibleColumns: PRESET_VIEW_COLUMNS.sales_recap,
  },
];

type StorageUserId = string | number | null | undefined;

const storageScope = (userId: StorageUserId, clientId: string | number) => `${userId ?? 'anonymous'}-${clientId}`;

export const getTopSellersSavedViewsStorageKey = (userId: StorageUserId, clientId: string | number) =>
  `top-sellers-saved-views-${storageScope(userId, clientId)}`;

export const getTopSellersColumnOrderStorageKey = (userId: StorageUserId, clientId: string | number) =>
  `top-sellers-column-order-${storageScope(userId, clientId)}`;

export const getTopSellersColumnWidthsStorageKey = (userId: StorageUserId, clientId: string | number) =>
  `top-sellers-column-widths-${storageScope(userId, clientId)}`;

export const getTopSellersPinnedColumnsStorageKey = (userId: StorageUserId, clientId: string | number) =>
  `top-sellers-pinned-columns-${storageScope(userId, clientId)}`;

export const TOP_SELLERS_SAVED_VIEWS_STORAGE_PREFIX = 'top-sellers';

// What the server stores for a view: everything that restores the report except its identity.
export type TopSellersViewPayload = Omit<SavedViewItem, 'id' | 'name' | 'isDefault'>;

// A user's saved view as listed with the built-ins: the record's id and sharing details alongside.
export interface StoredSavedViewItem extends SavedViewItem {
  savedViewId: number;
  visibility: SavedViewVisibility;
  ownerName: string | null;
  canEdit: boolean;
}

export const toSavedViewItem = (record: SavedViewRecord<TopSellersViewPayload>): StoredSavedViewItem => ({
  ...record.payload,
  id: `saved-${record.id}`,
  name: record.name,
  savedViewId: record.id,
  visibility: record.visibility,
  ownerName: record.ownerName,
  canEdit: record.canEdit,
});

const BUILT_IN_SAVED_VIEW_IDS = new Set(BUILT_IN_SAVED_VIEWS.map((view) => view.id));

/**
 * This browser's views for the user + client, ready to import. Built-in views are never imported,
 * and entries that couldn't be applied (no id, view, rank metric, size or columns) are skipped.
 */
export const loadLocalTopSellersSavedViews = (storageKey: string): LocalSavedView<TopSellersViewPayload>[] => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(window.localStorage.getItem(storageKey) ?? '[]');
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];

  return parsed
    .filter((view): view is SavedViewItem =>
      typeof view === 'object' && view !== null
      && (typeof view.id === 'string' || typeof view.id === 'number')
      && !BUILT_IN_SAVED_VIEW_IDS.has(String(view.id))
      && typeof view.view === 'string'
      && typeof view.rankBy === 'string'
      && typeof view.topN === 'number'
      && Array.isArray(view.visibleColumns))
    .map(({ id, name, isDefault, ...payload }) => ({
      legacyId: String(id),
      name: (typeof name === 'string' && name.trim() ? name.trim() : 'Untitled view').slice(0, SAVED_VIEW_NAME_MAX_LENGTH),
      payload,
    }));
};

export interface TopSellersReportProps {
  clientId?: string | number;
  epochMonth?: number;
  onClose?: () => void;
}

// --- EXACT DESIGN TOKEN CSS CONSTANTS MATCHING VENDOR PERFORMANCE ---
const metricCellCss = 'text-[#344054] text-[13px] font-medium leading-[19.5px]';
const lastYearCellCss = 'text-[#64748B] text-[13px] font-medium leading-[19.5px]';
// Group header subtotals -- same bold treatment as Vendor Performance's group rows.
const groupCellCss = 'text-[#101828] text-[13px] font-bold leading-[19.5px]';
// The table uses border-separate so pinned cells keep their borders; row dividers sit on the cells.
const ROW_DIVIDER = 'border-b border-[#E4E4E7]';
const FIRST_COL_SHADOW = 'shadow-[1px_0_0_0_#EAECF0]';
const subRowLabelCss = 'text-[#64748B] font-sans text-[12px] font-bold tracking-wide uppercase pl-12';

const RANK_PRODUCT_CELL_STYLE: React.CSSProperties = {
  color: '#101828',
  fontSize: '13px',
  fontWeight: 600,
  lineHeight: '20px',
};

export interface DiffResult {
  text: string;
  isPositive: boolean;
}

// CHANGE-row formatters for the "This Period / Last Year / Change" 3-row layout -- module-level
// (not component-scoped) since they're pure functions of their arguments with no dependency on
// component state/props, which also lets them be unit-tested directly (see
// TopSellersReport.component.test.tsx) without rendering the component or mocking the API. Same
// convention as Vendor Performance's diff formatters: sign + color only, no arrow icon, dollar
// diffs rounded to the nearest whole dollar.
export const fmtDiffCurrency = (thisVal: number, lyVal: number): DiffResult => {
  const diff = thisVal - lyVal;
  const isPositive = diff >= 0;
  const sign = isPositive ? '+' : '-';
  return { text: `${sign}$${Math.round(Math.abs(diff)).toLocaleString('en-US')}`, isPositive };
};

export const fmtDiffUnits = (thisVal: number, lyVal: number): DiffResult => {
  const diff = thisVal - lyVal;
  const isPositive = diff >= 0;
  const sign = isPositive ? '+' : '-';
  return { text: `${sign}${Math.round(Math.abs(diff)).toLocaleString('en-US')}`, isPositive };
};

// For percentage-point columns (gross_margin_pct, already 0-100 scale by the time it reaches
// TopSellerItem -- see mapApiRowToItem).
export const fmtDiffPctPts = (thisVal: number, lyVal: number): DiffResult => {
  const diff = thisVal - lyVal;
  const isPositive = diff >= 0;
  const sign = isPositive ? '+' : '-';
  return { text: `${sign}${Math.abs(diff).toFixed(1)}pts`, isPositive };
};

// GMROI is a ratio ("2.3x"), not a dollar/unit/percentage -- null on either side means "can't be
// computed" (no on-hand cost-value data), same as renderGMROIBadge()'s null branch, so the diff is
// undefined rather than a misleading number.
export const fmtDiffGmroi = (thisVal: number | null, lyVal: number | null): DiffResult | null => {
  if (thisVal === null || thisVal === undefined || lyVal === null || lyVal === undefined) {
    return null;
  }
  const diff = thisVal - lyVal;
  const isPositive = diff >= 0;
  const sign = isPositive ? '+' : '-';
  return { text: `${sign}${Math.abs(diff).toFixed(1)}x`, isPositive };
};

// Alternate display mode for the $/unit Change-row cells, toggled by "Show % change": percent
// change instead of an absolute difference. LY <= 0 has no meaningful percent change, so it
// renders as "-" rather than a divide-by-zero/infinite value -- same convention as Vendor
// Performance's fmtDiffPercentChange.
export const fmtDiffPercentChange = (thisVal: number, lyVal: number): DiffResult => {
  const diff = thisVal - lyVal;
  const isPositive = diff >= 0;
  if (lyVal <= 0) {
    return { text: '-', isPositive };
  }
  const pctChange = (diff / lyVal) * 100;
  const sign = pctChange >= 0 ? '+' : '-';
  return { text: `${sign}${Math.abs(pctChange).toFixed(1)}%`, isPositive };
};

export interface TopSellerGroupMetrics {
  sales_units: number;
  sales_dollars: number;
  avg_selling_price: number | null;
  avg_purchase_price: number | null;
  // Null when no priced item sold a unit.
  avg_retail_price: number | null;
  gross_margin_pct: number | null;
  cash_margin_pct: number | null;
  markdown_dollars: number;
  cash_margin_dollars: number;
  inventory_oh_units: number;
  inventory_oh_dollars: number;
  // Weighted from summed components, never averaged per item.
  markdown_pct: number | null;
  cost_of_purchase_pct: number | null;
  mmu_pct: number | null;
  weeks_of_supply: number | null;
  sell_through_pct: number | null;
  sell_through_start_inv_pct: number | null;
  suggested_reorder: number | null;
  on_order_units: number;
  // Null when an item's On Order $ is unknown at the selected basis.
  on_order_dollars: number | null;
  next_arrival_date: string | null;
  imu_pct: number | null;
}

// Group header row totals. Dollar/unit fields are summed; every ratio is recomputed from the
// summed components (e.g. cash margin % = summed cash margin $ / summed sales $), never averaged
// per item. gmroi/cmroi stay blank: their denominator (cost_value_oh) isn't exposed by the API.
export function sumTopSellerMetrics(items: TopSellerItem[]): TopSellerGroupMetrics {
  const sums = {
    sales_units: 0,
    sales_dollars: 0,
    markdown_dollars: 0,
    cash_margin_dollars: 0,
    inventory_oh_units: 0,
    inventory_oh_dollars: 0,
  };
  items.forEach((item) => {
    sums.sales_units += item.sales_units;
    sums.sales_dollars += item.sales_dollars;
    sums.markdown_dollars += item.markdown_dollars;
    sums.cash_margin_dollars += item.cash_margin_dollars;
    sums.inventory_oh_units += item.inventory_oh_units;
    sums.inventory_oh_dollars += item.inventory_oh_dollars;
  });
  const purchases = items.reduce((sum, item) => sum + (item.purchases_cost_dollars ?? 0), 0);
  const inflowRetail = items.reduce((sum, item) => sum + (item.inflow_dollars ?? 0), 0);
  const weeklyUnits = items.reduce((sum, item) => sum + (item.avg_weekly_units ?? 0), 0);
  // Full-price value of the units sold / those units -- weighted by units sold, so the group's
  // retail price lines up with its Avg Selling Price (the gap between them is the markdown).
  // Unpriced items and items with no units sold carry no weight.
  const pricedSoldItems = items.filter((item) => item.retail_price > 0 && item.sales_units > 0);
  const pricedSoldUnits = pricedSoldItems.reduce((sum, item) => sum + item.sales_units, 0);
  const avg_retail_price = pricedSoldUnits > 0
    ? pricedSoldItems.reduce((sum, item) => sum + item.retail_price * item.sales_units, 0) / pricedSoldUnits
    : null;
  const totalGrossProfit = items.reduce(
    (sum, item) => sum + (item.sales_dollars ?? 0) * ((item.gross_margin_pct ?? 0) / 100),
    0
  );
  const gross_margin_pct = sums.sales_dollars > 0
    ? (totalGrossProfit / sums.sales_dollars) * 100
    : null;

  const totalInflowUnits = items.reduce((sum, item) => sum + (item.inflow_units ?? 0), 0);
  const sell_through_pct = totalInflowUnits > 0
    ? (sums.sales_units / totalInflowUnits) * 100
    : null;
  const totalSellThroughUnits = sums.sales_units + sums.inventory_oh_units;
  const sell_through_start_inv_pct = totalSellThroughUnits > 0
    ? (sums.sales_units / totalSellThroughUnits) * 100
    : null;

  const onOrderItems = items.filter((item) => item.on_order_units > 0);
  const on_order_dollars = onOrderItems.some((item) => item.on_order_dollars == null)
    ? null
    : onOrderItems.reduce((sum, item) => sum + (item.on_order_dollars ?? 0), 0);
  const next_arrival_date = onOrderItems.reduce<string | null>(
    (earliest, item) => (item.next_arrival_date && (!earliest || item.next_arrival_date < earliest) ? item.next_arrival_date : earliest),
    null
  );
  // IMU inputs already cover every location, so a product listed once per store counts once.
  const imuInputs = new Map<string, Pick<TopSellerItem, 'imu_retail' | 'imu_cost'>>();
  items.forEach((item) => imuInputs.set(item.locationCode ? item.sku_id || item.id : item.id, item));
  const imuRetail = Array.from(imuInputs.values()).reduce((sum, item) => sum + (item.imu_retail ?? 0), 0);
  const imuCost = Array.from(imuInputs.values()).reduce((sum, item) => sum + (item.imu_cost ?? 0), 0);

  return {
    ...sums,
    // Summed dollars / summed units, never an average of the items' own prices.
    avg_selling_price: avgPerUnit(sums.sales_dollars, sums.sales_units),
    avg_purchase_price: avgPerUnit(purchases, totalInflowUnits),
    avg_retail_price,
    gross_margin_pct,
    cash_margin_pct: sums.sales_dollars > 0 ? (sums.cash_margin_dollars / sums.sales_dollars) * 100 : null,
    markdown_pct: sums.sales_dollars > 0 ? (sums.markdown_dollars / sums.sales_dollars) * 100 : null,
    cost_of_purchase_pct: sums.sales_dollars > 0 ? (purchases / sums.sales_dollars) * 100 : null,
    mmu_pct: computeMmuPct(inflowRetail, purchases, sums.markdown_dollars, sums.sales_dollars),
    weeks_of_supply: weeklyUnits > 0 ? sums.inventory_oh_units / weeklyUnits : null,
    sell_through_pct,
    sell_through_start_inv_pct,
    suggested_reorder: sumNullable(items.map((item) => item.suggested_reorder ?? null)),
    on_order_units: onOrderItems.reduce((sum, item) => sum + item.on_order_units, 0),
    on_order_dollars,
    next_arrival_date,
    imu_pct: imuRetail > 0 ? ((imuRetail - imuCost) / imuRetail) * 100 : null,
  };
}

// --- Sortable column headers ---
//
// Hybrid strategy, ported from Vendor Performance's SortableHeader/sortConfig/sortByConfig but
// adapted for a fundamental difference in how the two reports' data arrives:
//
// Vendor Performance's backend has no row-count truncation at all -- it returns the *entire*
// filtered/grouped result set every time, so client-side sorting is always 100% safe: it only ever
// reorders an already-complete array, never risks hiding a row that should have been included.
//
// Top Sellers' backend explicitly truncates to `top_n` rows via BigQuery's
// `QUALIFY ROW_NUMBER() ... <= :topN`, ranked by whichever of 4 metrics `rank_by` requests
// (RankByMetric), *before* rows ever leave BigQuery. So a column click here needs to do one of two
// different things depending on whether the backend actually supports ranking by that column:
//   - The 4 RankByMetric columns: trigger a real re-fetch with a new `rank_by` (setRankBy), so the
//     Top N is truly re-ranked, not just the current page's 25 rows reordered. BigQuery only ever
//     ranks these DESC (there's no ascending "Top N" -- that would mean "bottom N"), so clicking
//     one of these always requests true-top-N-by-that-metric rather than toggling a direction the
//     backend can't produce.
//   - Every other column: falls back to Vendor Performance's exact client-side sortByConfig
//     approach, reordering whatever page is already fetched. This is still useful information, but
//     does NOT change which rows are in the Top N -- it can't, without new backend rank_by support.
export type SortableMetricKey =
  | 'product_title' | 'vendor' | 'className' | 'classCode' | 'subClassName'
  | 'sales_units' | 'sales_dollars' | 'avg_selling_price' | 'retail_price' | 'color' | 'size'
  | 'avg_purchase_price'
  | 'markdown_dollars' | 'gross_margin_pct' | 'gmroi'
  | 'cash_margin_dollars' | 'cash_margin_pct' | 'cmroi'
  | 'inventory_oh_units' | 'inventory_oh_dollars' | 'suggested_reorder'
  | 'weeks_of_supply' | 'sell_through_pct' | 'sell_through_start_inv_pct' | 'markdown_pct' | 'cost_of_purchase_pct'
  | 'mmu_pct' | 'on_order_units' | 'on_order_dollars' | 'imu_pct';

export interface SortConfig {
  key: SortableMetricKey;
  direction: 'asc' | 'desc';
}

const BACKEND_RANKABLE_KEYS: readonly SortableMetricKey[] = ['sales_dollars', 'sales_units', 'gross_margin_pct', 'gmroi'];

export const isBackendRankableKey = (key: SortableMetricKey): key is RankByMetric =>
  (BACKEND_RANKABLE_KEYS as readonly string[]).includes(key);

export const compareForSort = (aVal: unknown, bVal: unknown, direction: 'asc' | 'desc'): number => {
  const result =
    typeof aVal === 'string' || typeof bVal === 'string'
      ? String(aVal ?? '').localeCompare(String(bVal ?? ''))
      : (Number(aVal) || 0) - (Number(bVal) || 0);
  return direction === 'asc' ? result : -result;
};

// Generic over any row/group shape this report sorts (TopSellerItem, HierarchyGroupRow) -- none declare
// an index signature, so the field lookup is cast to Record<string, unknown> inside the function
// body rather than constraining T itself, which would otherwise reject the concrete interfaces at
// the call site. Same pattern as Vendor Performance's sortByConfig().
export function sortByConfig<T>(items: T[], config: SortConfig | null): T[] {
  if (!config) return items;
  return [...items].sort((a, b) => {
    const aRow = a as Record<string, unknown>;
    const bRow = b as Record<string, unknown>;
    return compareForSort(aRow[config.key], bRow[config.key], config.direction);
  });
}

const getColumnLabel = (key: SortableMetricKey): string => {
  if (key === 'product_title') return 'Product';
  return COLUMN_TOGGLE_LIST.find((col) => col.key === key)?.label ?? key;
};

export const sortTypeOf = (key: SortableMetricKey): 'text' | 'numeric' => (TEXT_SORT_KEYS.includes(key) ? 'text' : 'numeric');

// The summary row's "sorted by" text, always with its direction -- including "low to high" on a
// server-ranked column, which lists the same Top N lowest-first (see handleSort).
export const sortSummaryLabel = (sort: SortConfig): string =>
  `${getColumnLabel(sort.key)} (${SORT_DIRECTION_LABELS[sortTypeOf(sort.key)][sort.direction]})`;

export const formatClassCode = (code: string | null | undefined): string => {
  if (!code) return '—';
  const clean = String(code).trim().replace(/^CL-?/i, '');
  if (!clean) return '—';
  const padded = /^\d+$/.test(clean) ? clean.padStart(3, '0') : clean;
  return `CL-${padded}`;
};

interface InfoTooltipProps {
  text: string;
  position?: 'top' | 'bottom';
  align?: 'left' | 'right' | 'center';
  textAlign?: 'left' | 'right' | 'center';
  // Suppresses the bubble, e.g. while the column's header menu is open over it.
  disabled?: boolean;
}

const InfoTooltip: React.FC<InfoTooltipProps> = ({
  text,
  position = 'top',
  align = 'center',
  textAlign,
  disabled = false,
}) => {
  const [isHovered, setIsHovered] = useState<boolean>(false);

  const verticalClass = position === 'bottom' ? 'top-full mt-2' : 'bottom-full mb-1.5';
  const horizontalClass =
    align === 'right'
      ? 'right-0'
      : align === 'left'
        ? 'left-0'
        : 'left-1/2 -translate-x-1/2';

  const textAlignmentClass =
    textAlign === 'right'
      ? 'text-right'
      : textAlign === 'center'
        ? 'text-center'
        : 'text-left';

  return (
    <div
      className={`relative inline-flex items-center cursor-pointer ml-1 ${isHovered ? 'z-[100]' : 'z-auto'}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Info className="w-3.5 h-3.5 text-[#98A2B3] hover:text-[#667085]" />
      {isHovered && !disabled && (
        <div
          className={`pointer-events-none z-[100] rounded-lg bg-[#101828] px-2.5 py-2 text-[11px] font-medium normal-case leading-snug text-white shadow-xl transition-all duration-150 whitespace-normal break-words absolute ${verticalClass} ${horizontalClass} ${textAlignmentClass} w-48 opacity-100 visible`}
        >
          {text}
        </div>
      )}
    </div>
  );
};

// Shared sortable <th> content, ported from Vendor Performance's SortableHeader: a clickable label
// showing a neutral icon until its column is the active sort, then a direction-specific chevron,
// plus the same InfoTooltip every other control in this report already uses. `activeSort` is the
// single source of truth for "what's sorted right now" -- see its derivation in the component body
// (sortConfig when a client-side sort is active, otherwise rankBy/desc since that's what's actually
// driving the fetched row order).
// Clicking the label opens the column's menu (sort, pin, hide) rather than sorting directly.
const SortableHeader: React.FC<{
  label: string;
  sortKey: SortableMetricKey;
  activeSort: SortConfig;
  isMenuOpen: boolean;
  onToggleMenu: () => void;
  menu?: React.ReactNode;
  align?: 'left' | 'right' | 'center';
  description?: string;
  isLastColumn?: boolean;
}> = ({ label, sortKey, activeSort, isMenuOpen, onToggleMenu, menu, align = 'right', description, isLastColumn = false }) => {
  const isActive = activeSort.key === sortKey;
  const sortIcon = isActive
    ? (activeSort.direction === 'asc' ? <ChevronUp className="w-3 h-3 shrink-0" /> : <ChevronDown className="w-3 h-3 shrink-0" />)
    : <ArrowUpDown className="w-3 h-3 text-[#94A3B8] shrink-0" />;
  const justify = align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : 'justify-start';
  const textAlignment = align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
  // If last column near table edge, align tooltip right; otherwise open to the right (left)
  // so it never extends behind the sticky first column or previous columns.
  const tooltipAlign = isLastColumn ? 'right' : 'left';
  return (
    <div className={`relative flex items-center ${justify} gap-1.5 w-full hover:z-50`} data-column-menu>
      {description && <InfoTooltip text={description} position="bottom" align={tooltipAlign} disabled={isMenuOpen} />}
      <button
        type="button"
        onClick={onToggleMenu}
        aria-haspopup="menu"
        aria-expanded={isMenuOpen}
        className={`flex items-center ${justify} gap-1 hover:text-[#09090B] transition-colors cursor-pointer ${textAlignment} ${isActive || isMenuOpen ? 'text-[#09090B]' : ''}`}
      >
        <span className={`whitespace-normal break-words leading-tight ${textAlignment}`}>{label}</span>
        {sortIcon}
      </button>
      {isMenuOpen && menu}
    </div>
  );
};

export const formatCurrency = (val: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val);

export const formatPercent = (val: number) => `${val.toFixed(1)}%`;

const MutedDash = () => <span className="text-[13px] text-[#98A2B3]">—</span>;

const formatRatio = (val: number | null | undefined) => (val === null || val === undefined ? '—' : `${val.toFixed(1)}x`);

// Standardized badge styling (flat, no stroke) matching Vendor Performance
export const renderGMROIBadge = (val: number | null) => {
  if (val === null || val === undefined) {
    return (
      <span
        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[12px] font-semibold font-mono bg-[#F9FAFB] text-[#667085]"
        title="No inventory cost-value data for this SKU -- GMROI can't be computed"
      >
        &mdash;
      </span>
    );
  }
  const tone = val >= 2.0 ? 'bg-[#ECFDF3] text-[#067A46]' : val >= 1.0 ? 'bg-[#FEF0C7] text-[#B54708]' : 'bg-[#FEF3F2] text-[#B42318]';
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[12px] font-semibold font-mono ${tone}`}>
      {val.toFixed(1)}x
    </span>
  );
};

export interface HierarchyGroupRow extends TopSellerGroupMetrics {
  key: string;
  label: string;
  // Class by Product only.
  classCode: string | null;
}

const groupKeyOf = (item: TopSellerItem, hierarchy: TopSellersHierarchy): string => {
  switch (hierarchy) {
    case 'category': return item.category ?? '';
    // By code, so same-named classes in different departments stay apart (matches the server's
    // TopSellersService::CLASS_PARTITION_EXPR); the name only for an item with no code.
    case 'class': return String(item.classCode ?? '').trim() || (item.className ?? '');
    case 'vendor': return item.vendor ?? '';
    case 'location': return item.locationCode ?? '';
    default: return '';
  }
};

// Class code without leading zeros ("010" -> "10", all zeros -> "0"), the form
// OtbPlanService::normalizeClassCode() also keys plan data by.
export const normalizeClassCode = (code: string): string => {
  const trimmed = code.trim();
  return trimmed.replace(/^0+/, '') || (trimmed === '' ? '' : '0');
};

// A group's plan / OTB figures. The API keys `otb.groups` by category name (upper-cased and
// trimmed) or, for classes, by class code without leading zeros. Null when that group has no
// plan data (or the hierarchy has none).
export const otbForGroup = (
  otb: TopSellersOtb | null | undefined,
  groupKey: string,
  hierarchy?: TopSellersHierarchy
): OtbMetrics | null => {
  const key = groupKey.trim().toUpperCase();
  return otb?.groups?.[hierarchy === 'class' ? normalizeClassCode(key) : key] ?? null;
};

// Groups keep the API's order, which is already sorted by the partition column.
export function groupItemsByHierarchy<T extends TopSellerItem>(
  items: T[],
  hierarchy: TopSellersHierarchy,
  locationNames: ReadonlyMap<string, string> = new Map()
): (HierarchyGroupRow & { items: T[]; count: number })[] {
  if (hierarchy === 'total') return [];
  const groups = new Map<string, T[]>();
  items.forEach((item) => {
    const key = groupKeyOf(item, hierarchy);
    groups.set(key, [...(groups.get(key) ?? []), item]);
  });
  return Array.from(groups.entries()).map(([key, groupItems]) => ({
    key,
    label: (hierarchy === 'location'
      ? locationNames.get(key) ?? key
      : hierarchy === 'class' ? groupItems[0]?.className || key : key) || 'Unassigned',
    classCode: hierarchy === 'class' ? groupItems[0]?.classCode ?? null : null,
    items: groupItems,
    count: groupItems.length,
    ...sumTopSellerMetrics(groupItems),
  }));
}

export type TopSellersRowKind = 'current' | 'ly' | 'change' | 'variant' | 'group';

export interface TopSellersCellContext {
  // $/unit Change-row formatters, switched by the "Show % change" toggle.
  diffCurrency: (thisVal: number, lyVal: number) => DiffResult;
  diffUnit: (thisVal: number, lyVal: number) => DiffResult;
  // Change in a per-unit price; follows "Show % change" like the others. Defaults to the cent diff.
  diffUnitPrice?: (thisVal: number, lyVal: number) => DiffResult;
}

// A missing renderer renders an empty cell; a Change renderer returning null renders a muted dash.
interface TopSellersColumnCells {
  current: (item: TopSellerItem) => React.ReactNode;
  ly?: (item: TopSellerItem) => React.ReactNode;
  change?: (item: TopSellerItem, ctx: TopSellersCellContext) => DiffResult | null;
  variant?: (variant: ProductVariant, parent: TopSellerItem) => React.ReactNode;
  group?: (group: HierarchyGroupRow) => React.ReactNode;
}

const SuggestedReorderCell: React.FC<{ units: number | null; markdownPct: number | null | undefined }> = ({ units, markdownPct }) => (
  <span className="inline-flex items-center justify-end gap-1">
    {isPromoDrivenRate(markdownPct) && (
      <span role="img" aria-label={PROMO_DRIVEN_RATE_TOOLTIP} title={PROMO_DRIVEN_RATE_TOOLTIP} className="inline-flex">
        <AlertTriangle className="w-3.5 h-3.5 text-amber-500" aria-hidden="true" />
      </span>
    )}
    {formatReorderUnits(units)}
  </span>
);

// Per-unit prices (SPS-5667 #21): cents, "—" without units, and a Change only when both periods
// have a price.
const formatUnitPrice = (val: number | null | undefined): string => (val == null ? '—' : fmtUnitPrice(val));

const unitPriceCells = (
  cur: 'avg_selling_price' | 'avg_purchase_price',
  ly: 'avg_selling_price_ly' | 'avg_purchase_price_ly'
): TopSellersColumnCells => ({
  current: (i) => formatUnitPrice(i[cur]),
  ly: (i) => formatUnitPrice(i[ly]),
  change: (i, ctx) => {
    const curVal = i[cur];
    const lyVal = i[ly];
    return curVal == null || lyVal == null ? null : (ctx.diffUnitPrice ?? fmtDiffUnitPrice)(curVal, lyVal);
  },
  variant: (v) => formatUnitPrice(v[cur]),
  group: (g) => formatUnitPrice(g[cur]),
});

export const TOP_SELLERS_COLUMN_CELLS: Record<TopSellersColumnKey, TopSellersColumnCells> = {
  vendor: {
    current: (i) => i.vendor,
    variant: (_v, p) => p.vendor,
  },
  className: {
    current: (i) => i.className,
    variant: (_v, p) => p.className,
  },
  classCode: {
    current: (i) => formatClassCode(i.classCode),
    variant: (_v, p) => formatClassCode(p.classCode),
    group: (g) => formatClassCode(g.classCode),
  },
  subClassName: {
    current: (i) => i.subClassName || '—',
    variant: (_v, p) => p.subClassName || '—',
  },
  sales_units: {
    current: (i) => i.sales_units.toLocaleString(),
    ly: (i) => (i.sales_units_ly ?? 0).toLocaleString(),
    change: (i, ctx) => ctx.diffUnit(i.sales_units, i.sales_units_ly ?? 0),
    variant: (v) => v.sales_units.toLocaleString(),
    group: (g) => g.sales_units.toLocaleString(),
  },
  sales_dollars: {
    current: (i) => formatCurrency(i.sales_dollars),
    ly: (i) => formatCurrency(i.sales_dollars_ly ?? 0),
    change: (i, ctx) => ctx.diffCurrency(i.sales_dollars, i.sales_dollars_ly ?? 0),
    variant: (v) => formatCurrency(v.sales_dollars),
    group: (g) => formatCurrency(g.sales_dollars),
  },
  avg_selling_price: unitPriceCells('avg_selling_price', 'avg_selling_price_ly'),
  avg_purchase_price: unitPriceCells('avg_purchase_price', 'avg_purchase_price_ly'),
  retail_price: {
    current: (i) => formatCurrency(i.retail_price),
    variant: (v) => formatCurrency(v.retail_price),
    group: (g) => (g.avg_retail_price != null ? formatCurrency(g.avg_retail_price) : <MutedDash />),
  },
  color: {
    current: (i) => i.color ?? '—',
    variant: (v) => v.color ?? '—',
  },
  size: {
    current: (i) => i.size ?? '—',
    variant: (v) => v.size ?? '—',
  },
  markdown_dollars: {
    current: (i) => formatCurrency(i.markdown_dollars),
    ly: (i) => formatCurrency(i.markdown_dollars_ly ?? 0),
    change: (i, ctx) => ctx.diffCurrency(i.markdown_dollars, i.markdown_dollars_ly ?? 0),
    variant: (v) => formatCurrency(v.markdown_dollars),
    group: (g) => formatCurrency(g.markdown_dollars),
  },
  gross_margin_pct: {
    current: (i) => formatPercent(i.gross_margin_pct),
    ly: (i) => (i.gross_margin_pct_ly == null ? '—' : formatPercent(i.gross_margin_pct_ly)),
    change: (i) => (i.gross_margin_pct_ly == null ? null : fmtDiffPctPts(i.gross_margin_pct, i.gross_margin_pct_ly)),
    variant: (v) => formatPercent(v.gross_margin_pct),
    group: (g) => (g.gross_margin_pct != null ? formatPercent(g.gross_margin_pct) : <MutedDash />),
  },
  gmroi: {
    current: (i) => renderGMROIBadge(i.gmroi),
    ly: (i) => renderGMROIBadge(i.gmroi_ly ?? null),
    change: (i) => fmtDiffGmroi(i.gmroi, i.gmroi_ly ?? null),
    variant: (v) => renderGMROIBadge(v.gmroi),
    group: () => <MutedDash />,
  },
  cash_margin_dollars: {
    current: (i) => formatCurrency(i.cash_margin_dollars),
    variant: (v) => formatCurrency(v.cash_margin_dollars),
    group: (g) => formatCurrency(g.cash_margin_dollars),
  },
  cash_margin_pct: {
    current: (i) => formatPercent(i.cash_margin_pct),
    variant: (v) => formatPercent(v.cash_margin_pct),
    group: (g) => (g.cash_margin_pct != null ? formatPercent(g.cash_margin_pct) : <MutedDash />),
  },
  cmroi: {
    current: (i) => formatRatio(i.cmroi),
    variant: (v) => formatRatio(v.cmroi),
    group: () => <MutedDash />,
  },
  // No prior-year inventory snapshot exists, so LY on-hand renders as a dash when absent.
  inventory_oh_units: {
    current: (i) => i.inventory_oh_units.toLocaleString(),
    ly: (i) => (i.inventory_oh_units_ly == null ? <MutedDash /> : i.inventory_oh_units_ly.toLocaleString()),
    change: (i, ctx) => (i.inventory_oh_units_ly == null ? null : ctx.diffUnit(i.inventory_oh_units, i.inventory_oh_units_ly)),
    variant: (v) => v.inventory_oh_units.toLocaleString(),
    group: (g) => g.inventory_oh_units.toLocaleString(),
  },
  inventory_oh_dollars: {
    current: (i) => formatCurrency(i.inventory_oh_dollars),
    ly: (i) => (i.inventory_oh_dollars_ly == null ? <MutedDash /> : formatCurrency(i.inventory_oh_dollars_ly)),
    change: (i, ctx) => (i.inventory_oh_dollars_ly == null ? null : ctx.diffCurrency(i.inventory_oh_dollars, i.inventory_oh_dollars_ly)),
    variant: (v) => formatCurrency(v.inventory_oh_dollars),
    group: (g) => formatCurrency(g.inventory_oh_dollars),
  },
  on_order_units: {
    current: (i) => formatOnOrderUnits(i.on_order_units, i.next_arrival_date),
    variant: (v) => formatOnOrderUnits(v.on_order_units, v.next_arrival_date),
    group: (g) => formatOnOrderUnits(g.on_order_units, g.next_arrival_date),
  },
  on_order_dollars: {
    current: (i) => formatOnOrderDollars(i.on_order_units, i.on_order_dollars),
    variant: (v) => formatOnOrderDollars(v.on_order_units, v.on_order_dollars),
    group: (g) => formatOnOrderDollars(g.on_order_units, g.on_order_dollars),
  },
  imu_pct: {
    current: (i) => formatOptionalPercent(i.imu_pct),
    variant: (v) => formatOptionalPercent(v.imu_pct),
    group: (g) => formatOptionalPercent(g.imu_pct),
  },
  suggested_reorder: {
    current: (i) => <SuggestedReorderCell units={i.suggested_reorder} markdownPct={i.markdown_pct} />,
    variant: (v) => <SuggestedReorderCell units={v.suggested_reorder} markdownPct={v.markdown_pct} />,
    group: (g) => <SuggestedReorderCell units={g.suggested_reorder} markdownPct={g.markdown_pct} />,
  },
  weeks_of_supply: {
    current: (i) => formatWeeks(i.weeks_of_supply),
    variant: (v) => formatWeeks(v.weeks_of_supply),
    group: (g) => formatWeeks(g.weeks_of_supply),
  },
  sell_through_pct: {
    current: (i) => formatOptionalPercent(i.sell_through_pct),
    ly: (i) => formatOptionalPercent(i.sell_through_pct_ly),
    change: (i) => (i.sell_through_pct === null || i.sell_through_pct === undefined || i.sell_through_pct_ly === null || i.sell_through_pct_ly === undefined ? null : fmtDiffPctPts(i.sell_through_pct, i.sell_through_pct_ly)),
    variant: (v) => formatOptionalPercent(v.sell_through_pct),
    group: (g) => formatOptionalPercent(g.sell_through_pct),
  },
  sell_through_start_inv_pct: {
    current: (i) => formatOptionalPercent(i.sell_through_start_inv_pct),
    ly: (i) => formatOptionalPercent(i.sell_through_start_inv_pct_ly),
    change: (i) => (i.sell_through_start_inv_pct === null || i.sell_through_start_inv_pct === undefined || i.sell_through_start_inv_pct_ly === null || i.sell_through_start_inv_pct_ly === undefined ? null : fmtDiffPctPts(i.sell_through_start_inv_pct, i.sell_through_start_inv_pct_ly)),
    variant: (v) => formatOptionalPercent(v.sell_through_start_inv_pct),
    group: (g) => formatOptionalPercent(g.sell_through_start_inv_pct),
  },
  markdown_pct: {
    current: (i) => formatOptionalPercent(i.markdown_pct),
    ly: (i) => formatOptionalPercent(i.markdown_pct_ly),
    change: (i) => (i.markdown_pct === null || i.markdown_pct_ly == null ? null : fmtDiffPctPts(i.markdown_pct, i.markdown_pct_ly)),
    variant: (v) => formatOptionalPercent(v.markdown_pct),
    group: (g) => formatOptionalPercent(g.markdown_pct),
  },
  mmu_pct: {
    current: (i) => formatOptionalPercent(i.mmu_pct),
    ly: (i) => formatOptionalPercent(i.mmu_pct_ly),
    change: (i) => (i.mmu_pct === null || i.mmu_pct_ly == null ? null : fmtDiffPctPts(i.mmu_pct, i.mmu_pct_ly)),
    variant: (v) => formatOptionalPercent(v.mmu_pct),
    group: (g) => formatOptionalPercent(g.mmu_pct),
  },
  cost_of_purchase_pct: {
    current: (i) => formatOptionalPercent(i.cost_of_purchase_pct),
    ly: (i) => formatOptionalPercent(i.cost_of_purchase_pct_ly),
    change: (i) => (i.cost_of_purchase_pct === null || i.cost_of_purchase_pct_ly == null ? null : fmtDiffPctPts(i.cost_of_purchase_pct, i.cost_of_purchase_pct_ly)),
    variant: (v) => formatOptionalPercent(v.cost_of_purchase_pct),
    group: (g) => formatOptionalPercent(g.cost_of_purchase_pct),
  },
};

const ROW_PADDING_Y: Record<TopSellersRowKind, string> = {
  current: 'py-3',
  ly: 'py-2',
  change: 'py-2',
  variant: 'py-2',
  group: 'py-2.5',
};

const ALIGN_CLASS = { left: 'text-left', right: 'text-right', center: 'text-center' } as const;

const diffTextClass = (isPositive: boolean) =>
  `text-[13px] font-semibold whitespace-nowrap ${isPositive ? 'text-emerald-700' : 'text-rose-700'}`;

const cellTextClass = (col: ColumnToggleConfig, kind: TopSellersRowKind): string => {
  if (kind === 'ly' || (kind === 'variant' && col.align !== 'left')) return lastYearCellCss;
  if (kind === 'change') return '';
  if (kind === 'group') return groupCellCss;
  return metricCellCss;
};

export type TopSellersCellRow =
  | { kind: 'current' | 'ly' | 'change'; item: TopSellerItem }
  | { kind: 'variant'; variant: ProductVariant; parent: TopSellerItem }
  | { kind: 'group'; group: HierarchyGroupRow };

export const renderTopSellersCell = (
  col: ColumnToggleConfig,
  row: TopSellersCellRow,
  ctx: TopSellersCellContext,
  tdClassName: string = ROW_DIVIDER,
  tdStyle?: React.CSSProperties
): React.ReactElement => {
  const cells = TOP_SELLERS_COLUMN_CELLS[col.key];
  let content: React.ReactNode = null;
  switch (row.kind) {
    case 'current':
      content = cells.current(row.item);
      break;
    case 'ly':
      content = cells.ly ? cells.ly(row.item) : null;
      break;
    case 'change':
      if (cells.change) {
        const diff = cells.change(row.item, ctx);
        content = diff ? <span className={diffTextClass(diff.isPositive)}>{diff.text}</span> : <MutedDash />;
      }
      break;
    case 'variant':
      content = cells.variant ? cells.variant(row.variant, row.parent) : null;
      break;
    case 'group':
      content = cells.group ? cells.group(row.group) : null;
      break;
  }
  return (
    <td
      key={col.key}
      className={`${col.padX} ${ROW_PADDING_Y[row.kind]} whitespace-nowrap ${ALIGN_CLASS[col.align]} ${cellTextClass(col, row.kind)} ${tdClassName}`}
      style={tdStyle}
    >
      {content}
    </td>
  );
};

// --- CSV export ---

// Text for the cells whose table renderer returns markup rather than a string.
const CSV_TEXT_OVERRIDES: Partial<Record<TopSellersColumnKey, Partial<Record<'current' | 'ly', (item: TopSellerItem) => string>>>> = {
  gmroi: { current: (i) => formatRatio(i.gmroi), ly: (i) => formatRatio(i.gmroi_ly ?? null) },
  suggested_reorder: { current: (i) => formatReorderUnits(i.suggested_reorder) },
};

// One item cell as plain text -- the same value the table shows, so the export matches the screen.
// A column with no Last Year / Change renderer is blank on those rows, as in the table.
export const topSellersCellText = (
  col: ColumnToggleConfig,
  item: TopSellerItem,
  kind: 'current' | 'ly' | 'change',
  ctx: TopSellersCellContext
): string => {
  const cells = TOP_SELLERS_COLUMN_CELLS[col.key];
  if (kind === 'change') {
    if (!cells.change) return '';
    return cells.change(item, ctx)?.text ?? '—';
  }
  const override = CSV_TEXT_OVERRIDES[col.key]?.[kind];
  if (override) return override(item);
  const render = kind === 'ly' ? cells.ly : cells.current;
  const value = render ? render(item) : null;
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  // <MutedDash /> is the only other markup a current / Last Year item cell renders.
  return React.isValidElement(value) && value.type === MutedDash ? '—' : '';
};

const CSV_VARIANT_TEXT_OVERRIDES: Partial<Record<TopSellersColumnKey, (variant: ProductVariant) => string>> = {
  gmroi: (v) => formatRatio(v.gmroi),
  suggested_reorder: (v) => formatReorderUnits(v.suggested_reorder),
};

// One SKU row cell as plain text. SKUs carry no Last Year figures, so their Last Year / Change
// rows show a dash where the product's row has a value and stay blank where it has none.
export const topSellersVariantCellText = (
  col: ColumnToggleConfig,
  variant: ProductVariant,
  parent: TopSellerItem,
  kind: 'current' | 'ly' | 'change'
): string => {
  const cells = TOP_SELLERS_COLUMN_CELLS[col.key];
  if (kind === 'ly') return cells.ly ? '—' : '';
  if (kind === 'change') return cells.change ? '—' : '';
  const override = CSV_VARIANT_TEXT_OVERRIDES[col.key];
  if (override) return override(variant);
  const value = cells.variant ? cells.variant(variant, parent) : null;
  return typeof value === 'string' || typeof value === 'number' ? String(value) : '';
};

export const CSV_ROW_TYPE = { header: 'Row Type', parent: 'Product', child: 'SKU' } as const;

export interface TopSellersCsvGroup {
  label: string;
  items: (TopSellerItem & { liveRank: number })[];
}

/**
 * The report as CSV, built from the rows the table renders (already sorted, filtered and
 * grouped): the hierarchy's group column (when grouped), Rank and Product, a Period column when
 * YOY is on, then the visible metric columns in their on-screen order. One row per item (three
 * with YOY).
 *
 * With variantsOf (By SKU), each product row is followed by a row per SKU it returns, and the file
 * gains a leading Row Type column, then SKU and the SKU-level columns (Color, Size) right after
 * Product, whatever their on-screen position. Those are blank on product rows; SKU rows repeat the
 * product and group on every line, leave Rank blank, and keep the product's YOY row layout. Product
 * rows keep their own totals, so filter on Row Type before summing the file.
 */
export function buildTopSellersCsv({
  groupHeader,
  groups,
  columns,
  headerLabel,
  ctx,
  showYOY,
  variantsOf,
}: {
  // Null for the flat (Total Store) list, which exports a single unlabelled group.
  groupHeader: string | null;
  groups: TopSellersCsvGroup[];
  columns: ColumnToggleConfig[];
  headerLabel: (col: ColumnToggleConfig) => string;
  ctx: TopSellersCellContext;
  showYOY: boolean;
  // The SKUs to list under each product, already filtered as the table filters them; omit for product-only rows.
  variantsOf?: (item: TopSellerItem) => readonly ProductVariant[];
}): string {
  const kinds: ('current' | 'ly' | 'change')[] = showYOY ? ['current', 'ly', 'change'] : ['current'];
  const groupCells = (group: TopSellersCsvGroup) => (groupHeader !== null ? [csvCell(group.label, true)] : []);
  const periodCells = (kind: 'current' | 'ly' | 'change') => (showYOY ? [CSV_PERIOD_LABELS[kind]] : []);
  const headerCells = (identity: string[], metricColumns: ColumnToggleConfig[]) =>
    [
      ...(groupHeader !== null ? [groupHeader] : []),
      ...identity,
      ...(showYOY ? ['Period'] : []),
      ...metricColumns.map(headerLabel),
    ].map((h) => csvCell(h, true));

  if (variantsOf) {
    const skuColumns = COLUMN_TOGGLE_LIST.filter((col) => col.skuLevelOnly);
    const metricColumns = columns.filter((col) => !col.skuLevelOnly);
    return buildParentChildCsv({
      header: headerCells(['Rank', 'Product', 'SKU', ...skuColumns.map(headerLabel)], metricColumns),
      rowType: CSV_ROW_TYPE,
      parents: groups.flatMap((group) => group.items.map((item) => ({ group, item }))),
      children: ({ item }) => variantsOf(item),
      parentLines: ({ group, item }) =>
        kinds.map((kind) => [
          ...groupCells(group),
          csvCell(String(item.liveRank)),
          csvCell(item.product_title ?? '', true),
          '',
          ...skuColumns.map(() => ''),
          ...periodCells(kind),
          ...metricColumns.map((col) => csvCell(topSellersCellText(col, item, kind, ctx), col.align === 'left')),
        ]),
      childLines: (variant, { group, item }) =>
        kinds.map((kind) => [
          ...groupCells(group),
          '',
          csvCell(item.product_title ?? '', true),
          csvCell(variant.sku_id ?? '', true),
          ...skuColumns.map((col) => csvCell(topSellersVariantCellText(col, variant, item, 'current'), true)),
          ...periodCells(kind),
          ...metricColumns.map((col) => csvCell(topSellersVariantCellText(col, variant, item, kind), col.align === 'left')),
        ]),
    });
  }

  const header = headerCells(['Rank', 'Product'], columns);

  const body = groups.flatMap((group) =>
    group.items.flatMap((item) =>
      kinds.map((kind) =>
        [
          ...groupCells(group),
          csvCell(String(item.liveRank)),
          csvCell(item.product_title ?? '', true),
          ...periodCells(kind),
          ...columns.map((col) => csvCell(topSellersCellText(col, item, kind, ctx), col.align === 'left')),
        ].join(',')
      )
    )
  );

  return [header.join(','), ...body].join('\r\n');
}

export const TopSellersReport: React.FC<TopSellersReportProps> = ({ clientId = 'demo' }) => {
  // --- STATE MANAGEMENT ---
  const [hierarchy, setHierarchy] = useState<TopSellersHierarchy>('total');
  const [skuGrain, setSkuGrain] = useState<SkuGrain>('product');
  const activeView: TopSellersView = skuGrain === 'sku' ? 'by_sku' : 'by_product';
  const isFlatListView = hierarchy === 'total';
  const productColumnHeader = isFlatListView ? 'Rank / Product' : 'Product';
  const selectedHierarchyConfig = HIERARCHY_OPTIONS.find((opt) => opt.key === hierarchy);
  const [dateRange, setDateRange] = useState<string>(DEFAULT_TOP_SELLERS_FILTERS.dateRange);
  // The custom range starts out as the default preset's dates.
  const [customEndDate, setCustomEndDate] = useState<string>(() => resolveDateRange(DEFAULT_TOP_SELLERS_DATE_RANGE).to);
  const [customStartDate, setCustomStartDate] = useState<string>(() => resolveDateRange(DEFAULT_TOP_SELLERS_DATE_RANGE).from);
  const [tempStartDate, setTempStartDate] = useState<string>(customStartDate);
  const [tempEndDate, setTempEndDate] = useState<string>(customEndDate);
  const [yoyComparison, setYoyComparison] = useState<boolean>(DEFAULT_TOP_SELLERS_FILTERS.yoyComparison);
  const [hideZeroSalesSkus, setHideZeroSalesSkus] = useState<boolean>(DEFAULT_TOP_SELLERS_FILTERS.hideZeroSalesSkus);
  const [topN, setTopN] = useState<number>(25);
  const [weeksOfSupplyTarget, setWeeksOfSupplyTarget] = useState<number>(DEFAULT_WEEKS_OF_SUPPLY_TARGET);
  const [reorderTargetMode, setReorderTargetMode] = useState<ReorderTargetMode>('weeks');
  const [coverThroughDate, setCoverThroughDate] = useState<string>('');
  // Date mode feeds Suggested Reorder (date - today) / 7 weeks; an empty or past date falls back to
  // the weeks target.
  const today = new Date();
  const coverDateWeeks = reorderTargetMode === 'date' ? weeksUntilCoverDate(coverThroughDate, today) : null;
  const reorderTargetWeeks = coverDateWeeks ?? weeksOfSupplyTarget;
  const [onOrderSpan, setOnOrderSpan] = useState<string>('3_months');
  const [priceBasis, setPriceBasis] = useState<PriceBasis>('retail');
  const [onOrderStartDate, setOnOrderStartDate] = useState<string>('');
  const [onOrderEndDate, setOnOrderEndDate] = useState<string>('');
  const [showCustomOnOrderSpan, setShowCustomOnOrderSpan] = useState<boolean>(false);
  const [draftOnOrderStart, setDraftOnOrderStart] = useState<string>('');
  const [draftOnOrderEnd, setDraftOnOrderEnd] = useState<string>('');
  const [rankBy, setRankBy] = useState<RankByMetric>('sales_dollars');
  // Non-null only while a client-side-only sort (a column the backend can't rank by) is active.
  // Null means "no override -- the effectively active sort is whatever rankBy last requested,"
  // see `activeSort` below.
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);

  // Values are location/class CODES (not display names)
  const [selectedLocations, setSelectedLocations] = useState<string[]>(DEFAULT_TOP_SELLERS_FILTERS.selectedLocations);
  const [excludeLocation, setExcludeLocation] = useState<boolean>(DEFAULT_TOP_SELLERS_FILTERS.excludeLocation);
  // Client-side search boxes inside each filter popover, narrowing a long options list -- never
  // sent to the API, filters only the already-fetched options/vendor list by name.
  const [locationFilterQuery, setLocationFilterQuery] = useState<string>('');

  const [selectedClasses, setSelectedClasses] = useState<string[]>(DEFAULT_TOP_SELLERS_FILTERS.selectedClasses);
  const [excludeClass, setExcludeClass] = useState<boolean>(DEFAULT_TOP_SELLERS_FILTERS.excludeClass);
  const [classFilterQuery, setClassFilterQuery] = useState<string>('');

  const [selectedCategories, setSelectedCategories] = useState<string[]>(DEFAULT_TOP_SELLERS_FILTERS.selectedCategories);
  const [excludeCategory, setExcludeCategory] = useState<boolean>(DEFAULT_TOP_SELLERS_FILTERS.excludeCategory);
  const [categoryFilterQuery, setCategoryFilterQuery] = useState<string>('');

  const [minSales, setMinSales] = useState<string>(DEFAULT_TOP_SELLERS_FILTERS.minSales);
  // Vendor names; empty = all vendors.
  const [selectedVendors, setSelectedVendors] = useState<string[]>(DEFAULT_TOP_SELLERS_FILTERS.selectedVendors);
  const [excludeVendor, setExcludeVendor] = useState<boolean>(DEFAULT_TOP_SELLERS_FILTERS.excludeVendor);
  const [vendorFilterQuery, setVendorFilterQuery] = useState<string>('');
  const [selectedChannels, setSelectedChannels] = useState<string>(DEFAULT_TOP_SELLERS_FILTERS.selectedChannels);

  const [showPctChange, setShowPctChange] = useState<boolean>(DEFAULT_TOP_SELLERS_FILTERS.showPctChange);

  // Debounced into `debouncedSearch` -- search runs server-side, before ranking/limiting.
  const [searchMode, setSearchMode] = useState<SearchFieldMode>('product_name');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 350);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // --- LIVE DATA STATE ---
  const [apiRows, setApiRows] = useState<TopSellerApiRow[]>([]);
  // Plan / OTB figures for the active hierarchy -- null when it has no plan grain or no plan data.
  const [otb, setOtb] = useState<TopSellersOtb | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [locationOptions, setLocationOptions] = useState<DropdownOption[]>([]);
  const [classOptions, setClassOptions] = useState<DropdownOption[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<DropdownOption[]>([]);
  const [channelOptionsList, setChannelOptionsList] = useState<string[]>([]);
  const [vendorOptions, setVendorOptions] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;

    TopSellersApi.getDropdownOptions(clientId)
      .then((opts) => {
        if (cancelled) return;
        setLocationOptions(opts.locations);
        setClassOptions(opts.classes);
        setCategoryOptions(opts.categories);
      })
      .catch(() => {
        if (!cancelled) {
          setLocationOptions([]);
          setClassOptions([]);
          setCategoryOptions([]);
        }
      });

    TopSellersApi.getChannelOptions(clientId)
      .then((channels) => {
        if (!cancelled) setChannelOptionsList(channels);
      })
      .catch(() => {
        if (!cancelled) setChannelOptionsList([]);
      });

    // The client's full vendor list, loaded once -- not built from the vendors the current Top N contains.
    TopSellersApi.getVendorOptions(clientId)
      .then((vendors) => {
        if (!cancelled) setVendorOptions(vendors);
      })
      .catch(() => {
        if (!cancelled) setVendorOptions([]);
      });

    return () => {
      cancelled = true;
    };
  }, [clientId]);

  // Filtering, search, ranking, and top-N limiting all happen server-side.
  useEffect(() => {
    let cancelled = false;
    const { from, to } = resolveDateRange(dateRange, customStartDate, customEndDate);

    setLoading(true);
    setLoadError(null);

    TopSellersApi.getTopSellers({
      clientId,
      from,
      to,
      view: activeView,
      hierarchy,
      rankBy,
      topN,
      locationCodes: selectedLocations,
      classCodes: selectedClasses,
      categoryIds: selectedCategories,
      excludeLocations: excludeLocation,
      excludeClasses: excludeClass,
      excludeCategories: excludeCategory,
      vendorIds: selectedVendors,
      excludeVendors: excludeVendor,
      channelIds: selectedChannels !== ALL_CHANNELS ? [selectedChannels] : undefined,
      minSales: minSales === NO_MIN_SALES || minSales === 'none' || minSales === 'Any' || !minSales ? undefined : Number(String(minSales).replace(/[^0-9.]/g, '')),
      includeLy: yoyComparison,
      searchField: searchMode,
      search: debouncedSearch,
      onOrderSpan,
      onOrderStartDate: onOrderSpan === 'custom' ? onOrderStartDate : undefined,
      onOrderEndDate: onOrderSpan === 'custom' ? onOrderEndDate : undefined,
    })
      .then((res) => {
        if (cancelled) return;
        const newRows = res?.rows ?? [];
        setApiRows(newRows);
        setOtb(res?.otb ?? null);
        // A failed query still answers 200 with whatever loaded; say so rather than show a quiet empty table.
        if (res?.query_error) setLoadError(res.query_error);
        if (activeView === 'by_sku') {
          const autoExpanded: Record<string, boolean> = {};
          newRows.forEach((r) => {
            if (r.variants && r.variants.length > 0) {
              autoExpanded[rowIdentity(r)] = true;
            }
          });
          setExpandedRowIds(autoExpanded);
        }
      })
      .catch(() => {
        if (cancelled) return;
        setApiRows([]);
        setOtb(null);
        setLoadError('Failed to load Top Sellers data. Please try again.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    clientId,
    activeView,
    hierarchy,
    dateRange,
    customStartDate,
    customEndDate,
    rankBy,
    topN,
    selectedLocations,
    selectedClasses,
    selectedCategories,
    excludeLocation,
    excludeClass,
    excludeCategory,
    selectedVendors,
    excludeVendor,
    selectedChannels,
    minSales,
    yoyComparison,
    searchMode,
    debouncedSearch,
    onOrderSpan,
    onOrderStartDate,
    onOrderEndDate,
  ]);

  const [expandedRowIds, setExpandedRowIds] = useState<Record<string, boolean>>({});

  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    COLUMN_TOGGLE_LIST.forEach((col) => {
      if (!col.skuLevelOnly) initial[col.key] = col.defaultVisible;
    });
    return initial;
  });

  const auth = AuthProvider.useAuth() as { user?: { id?: string | number } | null } | null;
  const userId = auth?.user?.id ?? null;
  const { columnOrder, updateColumnOrder, resetColumnOrder, getDragProps, dragClass } = useDraggableColumnOrder(
    DEFAULT_COLUMN_ORDER,
    getTopSellersColumnOrderStorageKey(userId, clientId)
  );
  // Columns pinned from a header menu sit at the front of columnOrder, in the order they were pinned.
  const { pinnedColumns, isPinned, setPinnedColumns, clearPinnedColumns } = usePinnedColumns(
    getTopSellersPinnedColumnsStorageKey(userId, clientId),
    DEFAULT_COLUMN_ORDER
  );
  const { widthOf, isResizing, getResizeHandleProps, resetColumnWidths } = useResizableColumns(
    getTopSellersColumnWidthsStorageKey(userId, clientId),
    getTopSellersDefaultColumnWidths(hierarchy),
    METRIC_COLUMN_WIDTH
  );

  const [activePopover, setActivePopover] = useState<string | null>(null);

  // The selected*/exclude* states are what's applied (and fetched); each popover edits a draft that
  // commits when it closes, so ticking boxes doesn't re-run the report query per tick.
  const locationDraft = usePendingFilterSelection(
    { codes: selectedLocations, exclude: excludeLocation },
    activePopover === 'location',
    (next) => {
      setSelectedLocations(next.codes);
      setExcludeLocation(next.exclude);
    }
  );
  const classDraft = usePendingFilterSelection(
    { codes: selectedClasses, exclude: excludeClass },
    activePopover === 'class',
    (next) => {
      setSelectedClasses(next.codes);
      setExcludeClass(next.exclude);
    }
  );
  const categoryDraft = usePendingFilterSelection(
    { codes: selectedCategories, exclude: excludeCategory },
    activePopover === 'category',
    (next) => {
      setSelectedCategories(next.codes);
      setExcludeCategory(next.exclude);
    }
  );
  const vendorDraft = usePendingFilterSelection(
    { codes: selectedVendors, exclude: excludeVendor },
    activePopover === 'vendor',
    (next) => {
      setSelectedVendors(next.codes);
      setExcludeVendor(next.exclude);
    }
  );
  const visibleVendorOptions = vendorOptions.filter((v) => v.toLowerCase().includes(vendorFilterQuery.trim().toLowerCase()));

  // Close active popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target?.closest('[data-popover-container]')) {
        setActivePopover(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyed `${hierarchy}:${groupKey}`; groups default to expanded.
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});

  const savedViewsStorageKey = getTopSellersSavedViewsStorageKey(userId, clientId);
  const { views: userSavedViews, saveView, deleteView } = useSavedViews<TopSellersViewPayload>({
    reportKey: 'top_sellers',
    clientId,
    userId,
    storagePrefix: TOP_SELLERS_SAVED_VIEWS_STORAGE_PREFIX,
    readLocalViews: () => (clientId ? loadLocalTopSellersSavedViews(savedViewsStorageKey) : []),
  });
  const savedViews: Array<SavedViewItem | StoredSavedViewItem> = [...BUILT_IN_SAVED_VIEWS, ...userSavedViews.map(toSavedViewItem)];
  const [newViewName, setNewViewName] = useState<string>('');
  const [newViewVisibility, setNewViewVisibility] = useState<SavedViewVisibility>('only_me');
  const [isSavingView, setIsSavingView] = useState<boolean>(false);
  const [savedViewError, setSavedViewError] = useState<string | null>(null);

  const togglePopover = (popoverName: string) => {
    setActivePopover(activePopover === popoverName ? null : popoverName);
  };

  // Custom dates fall back to the default preset's, as on first load.
  const applyCustomDates = (startDate?: string, endDate?: string) => {
    const { from: defaultFrom, to: defaultTo } = resolveDateRange(DEFAULT_TOP_SELLERS_DATE_RANGE);
    setCustomStartDate(startDate ?? defaultFrom);
    setCustomEndDate(endDate ?? defaultTo);
    setTempStartDate(startDate ?? defaultFrom);
    setTempEndDate(endDate ?? defaultTo);
  };

  // A preset range leaves the custom dates alone: they're unused until "Custom range..." is chosen.
  const applyFilters = (filters: TopSellersViewFilters) => {
    setDateRange(filters.dateRange);
    if (filters.dateRange === CUSTOM_DATE_RANGE) applyCustomDates(filters.customStartDate, filters.customEndDate);
    setSelectedLocations(keepIfSameCodes(filters.selectedLocations));
    setExcludeLocation(filters.excludeLocation);
    setSelectedClasses(keepIfSameCodes(filters.selectedClasses));
    setExcludeClass(filters.excludeClass);
    setSelectedCategories(keepIfSameCodes(filters.selectedCategories));
    setExcludeCategory(filters.excludeCategory);
    setMinSales(filters.minSales);
    setSelectedVendors(keepIfSameCodes(filters.selectedVendors));
    setExcludeVendor(filters.excludeVendor);
    setSelectedChannels(filters.selectedChannels);
    setYoyComparison(filters.yoyComparison);
    setShowPctChange(filters.showPctChange);
    setHideZeroSalesSkus(filters.hideZeroSalesSkus);
  };

  // A leftover search in a filter popover could hide the options just selected.
  const clearFilterQueries = () => {
    setLocationFilterQuery('');
    setClassFilterQuery('');
    setCategoryFilterQuery('');
    setVendorFilterQuery('');
  };

  const handleResetFilters = () => {
    applyFilters(DEFAULT_TOP_SELLERS_FILTERS);
    applyCustomDates();
    clearFilterQueries();
    setHierarchy('total');
    setSkuGrain('product');
    setTopN(25);
    setWeeksOfSupplyTarget(DEFAULT_WEEKS_OF_SUPPLY_TARGET);
    setReorderTargetMode('weeks');
    setCoverThroughDate('');
    setOnOrderSpan('3_months');
    setOnOrderStartDate('');
    setOnOrderEndDate('');
    setShowCustomOnOrderSpan(false);
    setPriceBasis('retail');
    setRankBy('sales_dollars');
    setSortConfig(null);
    setSearchQuery('');
    setSearchMode('product_name');
    setActivePopover(null);
    resetColumnOrderAndPins();
  };

  // Hybrid sort: the 4 RankByMetric columns trigger a real server-side re-rank (setRankBy, which
  // the data-fetch effect already depends on); every other column falls back to a client-side
  // reorder of the already-fetched page via sortConfig. See the module-level comment above
  // SortableMetricKey for why these need different handling.
  // Set from a column header's menu. For a RankByMetric column both directions re-rank the Top N
  // by that column; "low to high" then shows that same Top N lowest-first (BigQuery only ranks
  // DESC -- an ascending rank would be a "bottom N", not top sellers).
  const handleSort = (key: SortableMetricKey, direction: ColumnSortDirection) => {
    if (isBackendRankableKey(key)) {
      setRankBy(key);
      setSortConfig(direction === 'asc' ? { key, direction } : null);
      return;
    }
    setSortConfig({ key, direction });
  };

  // Key of the column whose header menu is open (PRODUCT_COLUMN_KEY for the sticky first column).
  const [activeMenuColumn, setActiveMenuColumn] = useState<string | null>(null);
  const toggleColumnMenu = (key: string) => setActiveMenuColumn((open) => (open === key ? null : key));
  const closeColumnMenu = () => setActiveMenuColumn(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!(event.target as HTMLElement | null)?.closest('[data-column-menu]')) setActiveMenuColumn(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Pinning moves the column to just after the other pinned columns; unpinning returns it to its
  // default slot (after the columns still pinned).
  const togglePinnedColumn = (key: TopSellersColumnKey) => {
    if (isPinned(key)) {
      const remaining = pinnedColumns.filter((k) => k !== key);
      setPinnedColumns(remaining);
      updateColumnOrder(unpinColumn(columnOrder, key, remaining, DEFAULT_COLUMN_ORDER));
    } else {
      updateColumnOrder(pinColumn(columnOrder, key, pinnedColumns));
      setPinnedColumns([...pinnedColumns, key]);
    }
  };

  const resetColumnOrderAndPins = () => {
    resetColumnOrder();
    clearPinnedColumns();
  };

  // Pinned columns can't be dragged or dropped on, so a drag can never move a column ahead of them.
  const getColumnDragProps = (key: TopSellersColumnKey, axis: 'x' | 'y') =>
    isPinned(key) ? { draggable: false } : getDragProps(key, axis);

  // Single source of truth for "what's the active sort right now," for SortableHeader's
  // active/chevron state and the summary row's "sorted by X" text. When sortConfig is null, the
  // effectively active sort is whatever rankBy last requested (BigQuery only ever ranks DESC).
  const activeSort: SortConfig = sortConfig ?? { key: rankBy, direction: 'desc' };

  const toggleRowExpanded = (id: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setExpandedRowIds((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleApplySavedView = (sv: SavedViewItem) => {
    const layout = resolveSavedViewLayout(sv);
    setHierarchy(layout.hierarchy);
    setSkuGrain(layout.skuGrain);
    setRankBy(sv.rankBy);
    setTopN(sv.topN);
    // Saved views don't persist a client-side sort override -- clear any active one so it can't
    // point at a column this view doesn't even have visible.
    setSortConfig(null);
    const newVis: Record<string, boolean> = {};
    COLUMN_TOGGLE_LIST.forEach((c) => {
      newVis[c.key] = sv.visibleColumns.includes(c.key);
    });
    setVisibleColumns(newVis);
    if (sv.columnOrder) {
      // The saved order defines the layout; pins from the current session would no longer hold.
      updateColumnOrder(normalizeColumnOrder(sv.columnOrder, DEFAULT_COLUMN_ORDER));
      clearPinnedColumns();
    }
    if (sv.weeksOfSupplyTarget) setWeeksOfSupplyTarget(sv.weeksOfSupplyTarget);
    if (sv.coverThroughDate) {
      setReorderTargetMode('date');
      setCoverThroughDate(sv.coverThroughDate);
    } else if (sv.weeksOfSupplyTarget) {
      setReorderTargetMode('weeks');
    }
    if (sv.onOrderSpan) setOnOrderSpan(sv.onOrderSpan);
    setOnOrderStartDate(sv.onOrderStartDate ?? '');
    setOnOrderEndDate(sv.onOrderEndDate ?? '');
    setShowCustomOnOrderSpan(false);
    setPriceBasis(sv.priceBasis ?? 'retail');
    const filters = resolveSavedViewFilters(sv.filters);
    if (filters) {
      applyFilters(filters);
      clearFilterQueries();
    }
    setActivePopover(null);
  };

  const handleSaveCurrentView = async () => {
    const name = newViewName.trim();
    if (!name || isSavingView) return;
    const payload: TopSellersViewPayload = {
      view: activeView,
      hierarchy,
      rankBy: rankBy,
      topN: topN,
      visibleColumns: visibleOrderedColumns.map((col) => col.key),
      columnOrder,
      weeksOfSupplyTarget,
      coverThroughDate: coverDateWeeks !== null ? coverThroughDate : undefined,
      onOrderSpan,
      onOrderStartDate,
      onOrderEndDate,
      priceBasis,
      filters: {
        dateRange,
        ...(dateRange === CUSTOM_DATE_RANGE ? { customStartDate, customEndDate } : {}),
        selectedLocations,
        excludeLocation,
        selectedClasses,
        excludeClass,
        selectedCategories,
        excludeCategory,
        selectedVendors,
        excludeVendor,
        selectedChannels,
        minSales,
        yoyComparison,
        showPctChange,
        hideZeroSalesSkus,
      },
    };
    setIsSavingView(true);
    setSavedViewError(null);
    try {
      await saveView({ name, visibility: newViewVisibility, payload });
      setNewViewName('');
      setNewViewVisibility('only_me');
    } catch (error) {
      console.error('Failed to save top sellers view', error);
      setSavedViewError('Could not save this view. Please try again.');
    } finally {
      setIsSavingView(false);
    }
  };

  const handleDeleteSavedView = async (sv: StoredSavedViewItem, e: React.MouseEvent) => {
    e.stopPropagation();
    setSavedViewError(null);
    try {
      await deleteView(sv.savedViewId);
    } catch (error) {
      console.error('Failed to delete top sellers view', error);
      setSavedViewError(`Could not delete "${sv.name}". Please try again.`);
    }
  };

  const mappedItems = useMemo(
    () =>
      apiRows.map((row) =>
        withPriceBasis(applySuggestedReorder(mapApiRowToItem(row, yoyComparison), reorderTargetWeeks), priceBasis)
      ),
    [apiRows, yoyComparison, reorderTargetWeeks, priceBasis]
  );

  const totalStoreItems = useMemo(() => (isFlatListView ? mappedItems : []), [isFlatListView, mappedItems]);

  const locationNames = useMemo(
    () => new Map(locationOptions.map((opt) => [opt.code, opt.name || opt.code])),
    [locationOptions]
  );

  const hierarchyGroups = useMemo(
    () => groupItemsByHierarchy(mappedItems, hierarchy, locationNames),
    [mappedItems, hierarchy, locationNames]
  );

  // Applies the client-side-only branch of the hybrid sort (see handleSort above) to whichever
  // dataset is about to render/export. When sortConfig is null (no client override active -- either
  // a RankByMetric column drove the fetch order, or nothing's been clicked yet), these are
  // pass-throughs and the natural fetched order is used as-is.
  const sortedTotalStoreItems = useMemo(
    () => (sortConfig ? sortByConfig(totalStoreItems, sortConfig) : totalStoreItems),
    [totalStoreItems, sortConfig]
  );

  const sortedHierarchyGroups = useMemo(() => {
    if (!sortConfig) return hierarchyGroups;
    // Sorting by the grouping column reorders the groups; any other column reorders items within each group.
    const isGroupColumn = (hierarchy === 'class' && sortConfig.key === 'className') || (hierarchy === 'vendor' && sortConfig.key === 'vendor');
    if (isGroupColumn) {
      return [...hierarchyGroups].sort((a, b) => compareForSort(a.label, b.label, sortConfig.direction));
    }
    return hierarchyGroups.map((group) => ({ ...group, items: sortByConfig(group.items, sortConfig) }));
  }, [hierarchyGroups, hierarchy, sortConfig]);

  // Change-row $/unit formatters follow the "Show % change" toggle.
  const diffCurrency = showPctChange ? fmtDiffPercentChange : fmtDiffCurrency;
  const diffUnit = showPctChange ? fmtDiffPercentChange : fmtDiffUnits;
  const diffUnitPrice = showPctChange ? fmtDiffPercentChange : fmtDiffUnitPrice;

  const cellContext: TopSellersCellContext = { diffCurrency, diffUnit, diffUnitPrice };
  const orderedColumns = columnOrder
    .map((key) => COLUMN_CONFIG_BY_KEY.get(key))
    .filter((col): col is ColumnToggleConfig => col !== undefined);
  const visibleOrderedColumns = orderedColumns.filter((col) => isColumnVisibleInView(col, activeView, visibleColumns));

  // The only way metric cells are rendered, so every row matches the header's columns and order.
  const renderCells = (row: TopSellersCellRow, tdClassName?: string, tdStyle?: React.CSSProperties) =>
    visibleOrderedColumns.map((col) => renderTopSellersCell(col, row, cellContext, tdClassName, tdStyle));

  const headerLabelOf = (col: ColumnToggleConfig) => columnHeaderLabel(col, reorderTargetWeeks, priceBasis);

  // Every item listed, collapsed groups included, as the CSV exports them.
  const totalRow = useMemo<HierarchyGroupRow>(
    () => ({ key: 'total', label: 'Total', classCode: null, ...sumTopSellerMetrics(mappedItems) }),
    [mappedItems]
  );

  const { scrollRef, theadRef, registerGroupBody, updateScrollState, stickyTop, headShadow, footShadow, groupShadow } = useStickyTableRows();
  useEffect(() => {
    updateScrollState();
  }, [updateScrollState, loading, mappedItems, hierarchy, expandedGroups, expandedRowIds, yoyComparison, hideZeroSalesSkus]);

  // Exports exactly what the table lists: the same sorted rows and groups (collapsed groups
  // included), the visible columns in their on-screen order and header text, Last Year /
  // Change rows when YOY is on, and in By SKU each product's SKUs under the same zero-sales
  // filter (collapsed products included).
  const handleExportCSV = () => {
    const csv = buildTopSellersCsv({
      groupHeader: isFlatListView ? null : HIERARCHY_GROUP_NOUN[hierarchy as Exclude<TopSellersHierarchy, 'total'>],
      groups: isFlatListView ? [{ label: '', items: sortedTotalStoreItems }] : sortedHierarchyGroups,
      columns: visibleOrderedColumns,
      headerLabel: headerLabelOf,
      ctx: cellContext,
      showYOY: yoyComparison,
      variantsOf: activeView === 'by_sku' ? (item) => filterZeroSalesVariants(item.variants, hideZeroSalesSkus) : undefined,
    });

    downloadCsv(csv, `top-sellers-${toLocalIsoDate(new Date())}-${hierarchy}-${skuGrain}.csv`);
  };

  // Standardized control classes matching Vendor Performance
  const filterBtnStyle =
    'h-[34px] bg-white border border-[#D0D5DD] rounded-[9px] px-2.5 text-[13px] font-semibold text-[#344054] text-left leading-[20px] flex items-center justify-between gap-1.5 shadow-2xs hover:bg-[#F9FAFB] transition-colors cursor-pointer whitespace-nowrap';

  const labelStyle = 'text-[12px] font-medium text-[#71717A] leading-[18px] whitespace-nowrap';

  // "By SKU" sub-rows, identified by "Color / Size"; vendor/class come from the parent product.
  const renderVariantRows = (variants: ProductVariant[], parentItem: TopSellerItem) => {
    return variants.map((variant) => {
      const label = variant.color && variant.size
        ? `${variant.color} / ${variant.size}`
        : variant.color || variant.size || formatSkuLabel(variant.sku_id);

      return (
        <tr key={variant.id} className="bg-slate-50/80 hover:bg-slate-100/80 transition text-xs border-b border-slate-100">
          <td className="sticky left-0 bg-slate-50/90 z-10 pl-12 pr-4 py-2 border-r border-b border-[#E4E4E7] font-medium text-slate-700 shadow-[1px_0_0_0_#EAECF0] whitespace-nowrap text-[13px]">
            {label}
          </td>

          {renderCells({ kind: 'variant', variant, parent: parentItem })}
        </tr>
      );
    });
  };

  // One item as a This Period row, plus Last Year / Change rows when YOY is on, then any
  // expanded variant rows.
  const renderItemRows = (item: TopSellerItem & { liveRank: number }) => {
    const isRowExpanded = expandedRowIds[item.id] ?? false;
    const visibleVariants = filterZeroSalesVariants(item.variants, hideZeroSalesSkus);

    return (
      <React.Fragment key={item.id}>
        {/* ROW: THIS PERIOD */}
        <tr
          onClick={() => toggleRowExpanded(item.id)}
          className="group hover:bg-[#F9FAFB] cursor-pointer transition-colors bg-white"
        >
          {/* Sticky Rank / Product Cell */}
          <td className="sticky left-0 bg-white group-hover:bg-[#F9FAFB] z-10 px-3.5 py-2.5 border-r border-b border-[#E4E4E7] shadow-[1px_0_0_0_#EAECF0] whitespace-nowrap">
            <div className="flex items-center gap-2" style={{ width: `calc(${widthOf(PRODUCT_COLUMN_KEY)}px - 1.75rem)` }}>
              {visibleVariants.length > 0 ? (
                <button
                  type="button"
                  onClick={(e) => toggleRowExpanded(item.id, e)}
                  className="p-0.5 text-[#98A2B3] hover:text-[#7F56D9] transition-colors"
                >
                  {isRowExpanded ? (
                    <ChevronDown className="w-3.5 h-3.5 text-[#7F56D9]" />
                  ) : (
                    <ChevronRight className="w-3.5 h-3.5 text-[#98A2B3]" />
                  )}
                </button>
              ) : (
                <span className="inline-block w-4 h-3.5 flex-shrink-0" />
              )}
              <span className="w-4 text-right flex-shrink-0 text-[#667085] text-[13px] font-medium">
                {item.liveRank}
              </span>
              {item.liveRank === 1 && (
                <TrendingUp className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
              )}
              <span className="truncate min-w-0 text-[#101828] text-[13px] font-semibold leading-[20px]" title={item.product_title}>{item.product_title}</span>
            </div>
          </td>

          {renderCells({ kind: 'current', item })}
        </tr>

        {yoyComparison && (
          <>
            {/* ROW: LAST YEAR */}
            <tr className="bg-white">
              <td className="sticky left-0 bg-white z-10 px-3.5 py-2 border-r border-b border-[#E4E4E7] shadow-[1px_0_0_0_#EAECF0] whitespace-nowrap">
                <span className={subRowLabelCss}>Last Year</span>
              </td>
              {renderCells({ kind: 'ly', item })}
            </tr>

            {/* ROW: CHANGE */}
            <tr className="bg-white border-b border-[#E4E4E7]">
              <td className="sticky left-0 bg-white z-10 px-3.5 py-2 border-r border-b border-[#E4E4E7] shadow-[1px_0_0_0_#EAECF0] whitespace-nowrap">
                <span className={subRowLabelCss}>Change</span>
              </td>
              {renderCells({ kind: 'change', item })}
            </tr>
          </>
        )}

        {isRowExpanded && renderVariantRows(visibleVariants, item)}
      </React.Fragment>
    );
  };

  return (
    <div className="w-full bg-[#FAFAFC] min-h-screen text-[#09090B] p-6 space-y-4 font-sans antialiased relative">
      {/* --- 1. HEADER BLOCK --- */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-1">
        <div className="flex items-start gap-3">
          {/* Soft Purple Icon Box */}
          <div className="w-9 h-9 rounded-xl bg-[#F5F0FF] border border-[#E9D7FE] flex items-center justify-center text-[#7C3AED] shadow-2xs shrink-0">
            <TrendingUp className="w-5 h-5 text-[#7C3AED]" />
          </div>
          <div>
            <h1 className="text-[22px] font-bold text-[#09090B] tracking-tight leading-tight">
              Top Sellers / Item Performance
            </h1>
            <p className="text-[13px] text-[#71717A] mt-1 leading-normal">
              Your top-performing SKUs ranked by a metric you choose — sales, units, margin, or GMROI.
            </p>
          </div>
        </div>

        {/* Top Right Action Buttons */}
        <div className="flex items-center gap-2">
          {/* Save View Popover Trigger */}
          <div className="relative" data-popover-container>
            <button
              type="button"
              onClick={() => togglePopover('saved_views')}
              className="h-[34px] px-[14px] bg-white border border-[#D0D5DD] rounded-[9px] text-[14px] font-semibold text-[#344054] hover:bg-[#F9FAFB] shadow-2xs flex items-center gap-1.5 transition-colors leading-[21px] cursor-pointer"
            >
              <Bookmark className="w-4 h-4 text-[#7F56D9]" />
              <span>Save view</span>
            </button>

            {/* Saved Views Popover Menu */}
            {activePopover === 'saved_views' && (
              <div className="absolute right-0 top-11 z-[60] w-72 bg-white rounded-2xl shadow-xl border border-[#E4E4E7] p-4 space-y-3 animate-in fade-in zoom-in-95 duration-100">
                <div className="flex items-center justify-between border-b border-[#EAECF0] pb-2">
                  <h4 className="text-[13px] font-bold text-[#101828]">Saved Views</h4>
                  <button onClick={() => setActivePopover(null)} className="text-[#98A2B3] hover:text-[#344054] cursor-pointer">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
                  {savedViews.map((sv) => {
                    const stored = 'savedViewId' in sv ? sv : null;
                    return (
                      <div
                        key={sv.id}
                        onClick={() => handleApplySavedView(sv)}
                        className="flex items-center justify-between gap-2 p-2 rounded-lg hover:bg-[#F9FAFB] cursor-pointer text-[13px] font-semibold text-[#344054] transition-colors"
                      >
                        <span className="flex flex-col min-w-0">
                          <span className="truncate">{sv.name}</span>
                          {stored?.visibility === 'shared' && (
                            <span className="text-[11px] font-medium text-[#667085] flex items-center gap-1 truncate">
                              <Users className="w-3 h-3 shrink-0" />
                              {stored.canEdit || !stored.ownerName ? 'Shared' : `Shared by ${stored.ownerName}`}
                            </span>
                          )}
                        </span>
                        {sv.isDefault && (
                          <span className="text-[11px] bg-[#F4EBFF] text-[#6941C6] px-1.5 py-0.5 rounded-full font-semibold">
                            Default
                          </span>
                        )}
                        {stored?.canEdit && (
                          <button
                            type="button"
                            onClick={(e) => handleDeleteSavedView(stored, e)}
                            aria-label={`Delete saved view ${sv.name}`}
                            className="text-[#98A2B3] hover:text-[#B42318] cursor-pointer"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="border-t border-[#EAECF0] pt-3 space-y-2">
                  <input
                    type="text"
                    placeholder="New view name..."
                    value={newViewName}
                    maxLength={SAVED_VIEW_NAME_MAX_LENGTH}
                    onChange={(e) => setNewViewName(e.target.value)}
                    className="w-full px-3 py-1.5 text-[13px] border border-[#D0D5DD] rounded-[9px] focus:outline-none focus:ring-2 focus:ring-[#7F56D9] text-[#344054]"
                  />
                  <div className="flex items-center gap-2" role="group" aria-label="Visibility">
                    {([
                      ['only_me', 'Only me', Lock],
                      ['shared', 'Shared', Users],
                    ] as const).map(([value, label, Icon]) => (
                      <button
                        key={value}
                        type="button"
                        aria-pressed={newViewVisibility === value}
                        onClick={() => setNewViewVisibility(value)}
                        className={`flex-1 py-1.5 rounded-[9px] text-[12px] font-semibold flex items-center justify-center gap-1.5 transition-colors cursor-pointer ${newViewVisibility === value
                          ? 'bg-[#F4EBFF] text-[#6941C6] border border-[#D6BBFB]'
                          : 'bg-white border border-[#D0D5DD] text-[#344054] hover:bg-[#F9FAFB]'
                          }`}
                      >
                        <Icon className="w-3.5 h-3.5" /> {label}
                      </button>
                    ))}
                  </div>
                  {newViewVisibility === 'shared' && (
                    <p className="text-[11px] text-[#667085]">
                      Everyone with access to this client can see and apply this view. Only you can delete it.
                    </p>
                  )}
                  {savedViewError && (
                    <p role="alert" className="text-[12px] text-[#B42318]">{savedViewError}</p>
                  )}
                  <button
                    type="button"
                    onClick={handleSaveCurrentView}
                    disabled={isSavingView}
                    className="w-full py-1.5 bg-[#7F56D9] hover:bg-[#6941C6] text-white text-[13px] font-semibold rounded-[9px] shadow-xs transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    Save Current Config
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Export CSV Button -- off until there are rows to export, so it can't save a header-only file. */}
          <button
            type="button"
            onClick={handleExportCSV}
            disabled={loading || mappedItems.length === 0}
            className="h-[34px] px-[14px] bg-white border border-[#D0D5DD] rounded-[9px] text-[14px] font-semibold text-[#344054] hover:bg-[#F9FAFB] shadow-2xs flex items-center gap-1.5 transition-colors leading-[21px] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="w-4 h-4 text-[#667085]" />
            <span>Export CSV</span>
          </button>

          {/* Favorite Button */}
          <button
            type="button"
            className="h-[34px] w-[34px] bg-white border border-[#D0D5DD] rounded-[9px] text-[#667085] hover:text-[#09090B] hover:bg-[#F9FAFB] shadow-2xs flex items-center justify-center transition-colors cursor-pointer"
            title="Favorite"
          >
            <Star className="w-4 h-4 text-[#667085]" />
          </button>
        </div>
      </div>

      {/* --- 2. UNIFIED CONTROLS & FILTER CARD --- */}
      <div className="bg-white p-5 rounded-[16px] border border-[#E4E4E7] shadow-2xs relative z-30 flex flex-col gap-4">
        {/* ROW 1: Filters (Date Range, Location, Category, Class, Vendor, Channel, Min. Sales, On-Order & OTB Span) */}
        <div className="flex flex-wrap items-end gap-3 xl:gap-2.5">
          {/* 1. Date Range Custom Popover */}
          <div className="flex flex-col gap-1 relative min-w-[125px]" data-popover-container>
            <div className="flex items-center">
              <label className={labelStyle}>Date Range:</label>
              <InfoTooltip text="Select the date range for sales and performance metrics." />
            </div>
            <button
              type="button"
              onClick={() => {
                setTempStartDate(customStartDate);
                setTempEndDate(customEndDate);
                togglePopover('date_range');
              }}
              className={filterBtnStyle}
            >
              <span className="truncate">
                {dateRange === CUSTOM_DATE_RANGE && customStartDate && customEndDate
                  ? `${customStartDate} – ${customEndDate}`
                  : dateRange}
              </span>
              <ChevronDown className="w-3.5 h-3.5 text-[#667085] flex-shrink-0" />
            </button>

            {activePopover === 'date_range' && (
              <div className="absolute left-0 top-full mt-1.5 z-50 w-60 bg-white border border-[#E4E4E7] rounded-2xl shadow-xl p-2.5 flex flex-col gap-1 animate-in fade-in zoom-in-95 duration-100">
                {TOP_SELLERS_DATE_RANGE_PRESETS.map((opt) => {
                  const isSelected =
                    dateRange === opt ||
                    dateRange.toLowerCase() === opt.toLowerCase() ||
                    (opt === '3 Months' && (dateRange === 'Trailing 90 days' || dateRange.toLowerCase() === '3 months')) ||
                    (opt === '1 Month' && (dateRange === 'Trailing 30 days' || dateRange.toLowerCase() === '1 month')) ||
                    (opt === '6 Months' && (dateRange === 'Trailing 180 days' || dateRange.toLowerCase() === '6 months'));

                  return (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => {
                        setDateRange(opt);
                        if (opt !== CUSTOM_DATE_RANGE) {
                          setActivePopover(null);
                        }
                      }}
                      className={`w-full px-3 py-2 text-[13px] font-semibold rounded-[9px] flex items-center justify-between text-left transition-colors cursor-pointer leading-[21px] ${isSelected ? 'bg-[#F4F0FF] text-[#7F56D9]' : 'text-[#344054] hover:bg-[#F9FAFB]'
                        }`}
                    >
                      <span>{opt}</span>
                      {isSelected && <Check className="w-4 h-4 text-[#7F56D9]" />}
                    </button>
                  );
                })}

                {dateRange === CUSTOM_DATE_RANGE && (
                  <div className="mt-2 pt-2.5 border-t border-[#F2F4F7] flex flex-col gap-2">
                    <div className="flex flex-col gap-1">
                      <label className="text-[11px] font-semibold text-[#475467]">From</label>
                      <input
                        type="date"
                        max={tempEndDate || lastCompleteIsoDate()}
                        value={tempStartDate}
                        onChange={(e) => setTempStartDate(e.target.value)}
                        className="w-full px-2.5 py-1.5 text-[12px] font-medium text-[#344054] border border-[#D0D5DD] rounded-[8px] focus:outline-none focus:ring-1 focus:ring-[#7F56D9]"
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-[11px] font-semibold text-[#475467]">To</label>
                      <input
                        type="date"
                        max={lastCompleteIsoDate()}
                        min={tempStartDate}
                        value={tempEndDate}
                        onChange={(e) => {
                          const yesterdayIso = lastCompleteIsoDate();
                          const val = e.target.value;
                          setTempEndDate(val && val > yesterdayIso ? yesterdayIso : val);
                        }}
                        className="w-full px-2.5 py-1.5 text-[12px] font-medium text-[#344054] border border-[#D0D5DD] rounded-[8px] focus:outline-none focus:ring-1 focus:ring-[#7F56D9]"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        if (tempStartDate && tempEndDate) {
                          setCustomStartDate(tempStartDate);
                          setCustomEndDate(tempEndDate);
                          setActivePopover(null);
                        }
                      }}
                      disabled={!tempStartDate || !tempEndDate}
                      className="w-full mt-1 px-3 py-2 bg-[#7F56D9] text-white text-[12px] font-semibold rounded-[8px] hover:bg-[#6941C6] disabled:opacity-50 transition-colors cursor-pointer text-center"
                    >
                      Apply Range
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* 2. Location Multi-Select Popover */}
          <div className="flex flex-col gap-1 relative min-w-[95px]" data-popover-container>
            <div className="flex items-center">
              <label className={labelStyle}>Location:</label>
            </div>
            <button
              type="button"
              onClick={() => togglePopover('location')}
              className={filterBtnStyle}
            >
              <span className="truncate">
                {selectedLocations.length === 0
                  ? 'All locations'
                  : selectedLocations.length === 1
                    ? locationOptions.find((o) => o.code === selectedLocations[0])?.name || selectedLocations[0]
                    : `${selectedLocations.length} locations`}
              </span>
              <ChevronDown className="w-3.5 h-3.5 text-[#667085] flex-shrink-0" />
            </button>

            {activePopover === 'location' && (
              <div className="absolute left-0 top-full mt-1.5 z-50 w-64 bg-white border border-[#E4E4E7] rounded-2xl shadow-xl p-3.5 flex flex-col gap-2.5 animate-in fade-in zoom-in-95 duration-100">
                <div className="text-[13px] font-bold text-[#101828] border-b border-[#EAECF0] pb-2">Location</div>
                <input
                  type="text"
                  value={locationFilterQuery}
                  onChange={(e) => setLocationFilterQuery(e.target.value)}
                  placeholder="Search locations..."
                  className="h-8 w-full px-2.5 border border-[#E4E4E7] rounded-[8px] text-[13px] font-medium text-[#344054] bg-white focus:outline-none focus:ring-2 focus:ring-[#7F56D9] shadow-2xs"
                />
                <div className="flex flex-col gap-2 max-h-56 overflow-y-auto pr-1">
                  {locationOptions.filter((o) => o.name.toLowerCase().includes(locationFilterQuery.trim().toLowerCase())).length === 0 && (
                    <div className="text-[13px] text-[#94A3B8] italic px-1 py-2">No locations available</div>
                  )}
                  {locationOptions.filter((o) => o.name.toLowerCase().includes(locationFilterQuery.trim().toLowerCase())).map((loc) => {
                    const isChecked = locationDraft.pending.codes.includes(loc.code);
                    return (
                      <label key={loc.code} className="flex items-center gap-2.5 cursor-pointer text-[13px] font-semibold text-[#344054] hover:bg-[#F9FAFB] p-1 rounded-lg transition-colors">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => locationDraft.toggleCode(loc.code)}
                          className="w-4 h-4 accent-[#7F56D9] text-[#7F56D9] rounded border-[#D0D5DD]"
                        />
                        <span className="truncate">{loc.name}</span>
                      </label>
                    );
                  })}
                </div>
                <div className="border-t border-[#EAECF0] pt-2.5 space-y-2.5">
                  <label className="flex items-center gap-2 text-[12px] font-medium text-[#344054] cursor-pointer">
                    <input
                      type="checkbox"
                      checked={locationDraft.pending.exclude}
                      onChange={(e) => locationDraft.setExclude(e.target.checked)}
                      className="w-3.5 h-3.5 accent-[#7F56D9] rounded"
                    />
                    <span>Exclude selected instead</span>
                  </label>
                  <div className="flex items-center justify-between pt-1">
                    <button
                      type="button"
                      onClick={() => {
                        locationDraft.clearCodes();
                        setLocationFilterQuery('');
                      }}
                      className="text-[13px] font-semibold text-[#667085] hover:text-[#101828] cursor-pointer"
                    >
                      Reset
                    </button>
                    <button
                      type="button"
                      onClick={() => setActivePopover(null)}
                      className="px-4 py-1.5 bg-[#7F56D9] hover:bg-[#6941C6] text-white text-[13px] font-bold rounded-[9px] shadow-xs cursor-pointer"
                    >
                      Apply
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* 3. Category Multi-Select Popover */}
          <div className="flex flex-col gap-1 relative min-w-[95px]" data-popover-container>
            <div className="flex items-center">
              <label className={labelStyle}>Category:</label>
            </div>
            <button
              type="button"
              onClick={() => togglePopover('category')}
              className={filterBtnStyle}
            >
              <span className="truncate">
                {selectedCategories.length === 0
                  ? 'All categories'
                  : selectedCategories.length === 1
                    ? categoryOptions.find((o) => o.code === selectedCategories[0])?.name || selectedCategories[0]
                    : `${selectedCategories.length} categories`}
              </span>
              <ChevronDown className="w-3.5 h-3.5 text-[#667085] flex-shrink-0" />
            </button>

            {activePopover === 'category' && (
              <div className="absolute left-0 top-full mt-1.5 z-50 w-64 bg-white border border-[#E4E4E7] rounded-2xl shadow-xl p-3.5 flex flex-col gap-2.5 animate-in fade-in zoom-in-95 duration-100">
                <div className="text-[13px] font-bold text-[#101828] border-b border-[#EAECF0] pb-2">Category</div>
                <input
                  type="text"
                  value={categoryFilterQuery}
                  onChange={(e) => setCategoryFilterQuery(e.target.value)}
                  placeholder="Search categories..."
                  className="h-8 w-full px-2.5 border border-[#E4E4E7] rounded-[8px] text-[13px] font-medium text-[#344054] bg-white focus:outline-none focus:ring-2 focus:ring-[#7F56D9] shadow-2xs"
                />
                <div className="flex flex-col gap-2 max-h-56 overflow-y-auto pr-1">
                  {categoryOptions.filter((o) => o.name.toLowerCase().includes(categoryFilterQuery.trim().toLowerCase())).length === 0 && (
                    <div className="text-[13px] text-[#94A3B8] italic px-1 py-2">No categories available</div>
                  )}
                  {categoryOptions.filter((o) => o.name.toLowerCase().includes(categoryFilterQuery.trim().toLowerCase())).map((cat) => {
                    const isChecked = categoryDraft.pending.codes.includes(cat.code);
                    return (
                      <label key={cat.code} className="flex items-center gap-2.5 cursor-pointer text-[13px] font-semibold text-[#344054] hover:bg-[#F9FAFB] p-1 rounded-lg transition-colors">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => categoryDraft.toggleCode(cat.code)}
                          className="w-4 h-4 accent-[#7F56D9] text-[#7F56D9] rounded border-[#D0D5DD]"
                        />
                        <span className="truncate">{cat.name}</span>
                      </label>
                    );
                  })}
                </div>
                <div className="border-t border-[#EAECF0] pt-2.5 space-y-2.5">
                  <label className="flex items-center gap-2 text-[12px] font-medium text-[#344054] cursor-pointer">
                    <input
                      type="checkbox"
                      checked={categoryDraft.pending.exclude}
                      onChange={(e) => categoryDraft.setExclude(e.target.checked)}
                      className="w-3.5 h-3.5 accent-[#7F56D9] rounded"
                    />
                    <span>Exclude selected instead</span>
                  </label>
                  <div className="flex items-center justify-between pt-1">
                    <button
                      type="button"
                      onClick={() => {
                        categoryDraft.clearCodes();
                        setCategoryFilterQuery('');
                      }}
                      className="text-[13px] font-semibold text-[#667085] hover:text-[#101828] cursor-pointer"
                    >
                      Reset
                    </button>
                    <button
                      type="button"
                      onClick={() => setActivePopover(null)}
                      className="px-4 py-1.5 bg-[#7F56D9] hover:bg-[#6941C6] text-white text-[13px] font-bold rounded-[9px] shadow-xs cursor-pointer"
                    >
                      Apply
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* 4. Class Multi-Select Popover */}
          <div className="flex flex-col gap-1 relative min-w-[90px]" data-popover-container>
            <div className="flex items-center">
              <label className={labelStyle}>Class:</label>
            </div>
            <button
              type="button"
              onClick={() => togglePopover('class')}
              className={filterBtnStyle}
            >
              <span className="truncate">
                {selectedClasses.length === 0
                  ? 'All classes'
                  : selectedClasses.length === 1
                    ? selectedClassLabel(classOptions, selectedClasses[0])
                    : `${selectedClasses.length} classes`}
              </span>
              <ChevronDown className="w-3.5 h-3.5 text-[#667085] flex-shrink-0" />
            </button>

            {activePopover === 'class' && (
              <div className="absolute left-0 top-full mt-1.5 z-50 w-64 bg-white border border-[#E4E4E7] rounded-2xl shadow-xl p-3.5 flex flex-col gap-2.5 animate-in fade-in zoom-in-95 duration-100">
                <div className="text-[13px] font-bold text-[#101828] border-b border-[#EAECF0] pb-2">Class</div>
                <input
                  type="text"
                  value={classFilterQuery}
                  onChange={(e) => setClassFilterQuery(e.target.value)}
                  placeholder="Search classes..."
                  className="h-8 w-full px-2.5 border border-[#E4E4E7] rounded-[8px] text-[13px] font-medium text-[#344054] bg-white focus:outline-none focus:ring-2 focus:ring-[#7F56D9] shadow-2xs"
                />
                <div className="flex flex-col gap-2 max-h-56 overflow-y-auto pr-1">
                  {classOptions.filter((o) => matchesClassQuery(o, classFilterQuery)).length === 0 && (
                    <div className="text-[13px] text-[#94A3B8] italic px-1 py-2">No classes available</div>
                  )}
                  {classOptions.filter((o) => matchesClassQuery(o, classFilterQuery)).map((cls) => {
                    const isChecked = classDraft.pending.codes.includes(cls.code);
                    return (
                      <label key={cls.code} className="flex items-center gap-2.5 cursor-pointer text-[13px] font-semibold text-[#344054] hover:bg-[#F9FAFB] p-1 rounded-lg transition-colors">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => classDraft.toggleCode(cls.code)}
                          className="w-4 h-4 accent-[#7F56D9] text-[#7F56D9] rounded border-[#D0D5DD]"
                        />
                        <span className="min-w-0 flex items-center gap-1.5 whitespace-normal break-words">
                          {cls.code && (
                            <span className="px-1.5 py-0.5 rounded-[5px] bg-[#F2F4F7] text-[11px] font-mono font-semibold text-[#475467] shrink-0">
                              {cls.code}
                            </span>
                          )}
                          <span>{cls.name}</span>
                        </span>
                      </label>
                    );
                  })}
                </div>
                <div className="border-t border-[#EAECF0] pt-2.5 space-y-2.5">
                  <label className="flex items-center gap-2 text-[12px] font-medium text-[#344054] cursor-pointer">
                    <input
                      type="checkbox"
                      checked={classDraft.pending.exclude}
                      onChange={(e) => classDraft.setExclude(e.target.checked)}
                      className="w-3.5 h-3.5 accent-[#7F56D9] rounded"
                    />
                    <span>Exclude selected instead</span>
                  </label>
                  <div className="flex items-center justify-between pt-1">
                    <button
                      type="button"
                      onClick={() => {
                        classDraft.clearCodes();
                        setClassFilterQuery('');
                      }}
                      className="text-[13px] font-semibold text-[#667085] hover:text-[#101828] cursor-pointer"
                    >
                      Reset
                    </button>
                    <button
                      type="button"
                      onClick={() => setActivePopover(null)}
                      className="px-4 py-1.5 bg-[#7F56D9] hover:bg-[#6941C6] text-white text-[13px] font-bold rounded-[9px] shadow-xs cursor-pointer"
                    >
                      Apply
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* 5. Vendor Multi-Select Popover */}
          <div className="flex flex-col gap-1 relative min-w-[95px]" data-popover-container>
            <div className="flex items-center">
              <label className={labelStyle}>Vendor:</label>
            </div>
            <button
              type="button"
              onClick={() => togglePopover('vendor')}
              className={filterBtnStyle}
            >
              <span className="truncate">
                {selectedVendors.length === 0
                  ? 'All vendors'
                  : selectedVendors.length === 1
                    ? selectedVendors[0]
                    : `${selectedVendors.length} vendors`}
              </span>
              <ChevronDown className="w-3.5 h-3.5 text-[#667085] flex-shrink-0" />
            </button>

            {activePopover === 'vendor' && (
              <div className="absolute left-0 top-full mt-1.5 z-50 w-64 bg-white border border-[#E4E4E7] rounded-2xl shadow-xl p-3.5 flex flex-col gap-2.5 animate-in fade-in zoom-in-95 duration-100">
                <div className="text-[13px] font-bold text-[#101828] border-b border-[#EAECF0] pb-2">Vendor</div>
                <input
                  type="text"
                  value={vendorFilterQuery}
                  onChange={(e) => setVendorFilterQuery(e.target.value)}
                  placeholder="Search vendors..."
                  className="h-8 w-full px-2.5 border border-[#E4E4E7] rounded-[8px] text-[13px] font-medium text-[#344054] bg-white focus:outline-none focus:ring-2 focus:ring-[#7F56D9] shadow-2xs"
                />
                <div className="flex items-center justify-between text-[12px] font-semibold">
                  <button
                    type="button"
                    onClick={() => vendorDraft.addCodes(visibleVendorOptions)}
                    disabled={visibleVendorOptions.length === 0}
                    className="text-[#7F56D9] hover:text-[#6941C6] cursor-pointer disabled:text-[#98A2B3] disabled:cursor-not-allowed"
                  >
                    Select all
                  </button>
                  <button
                    type="button"
                    onClick={() => vendorDraft.clearCodes()}
                    disabled={vendorDraft.pending.codes.length === 0}
                    className="text-[#667085] hover:text-[#101828] cursor-pointer disabled:text-[#98A2B3] disabled:cursor-not-allowed"
                  >
                    Clear
                  </button>
                </div>
                <div className="flex flex-col gap-2 max-h-56 overflow-y-auto pr-1">
                  {visibleVendorOptions.length === 0 && (
                    <div className="text-[13px] text-[#94A3B8] italic px-1 py-2">No vendors available</div>
                  )}
                  {visibleVendorOptions.map((vendor) => (
                    <label key={vendor} className="flex items-center gap-2.5 cursor-pointer text-[13px] font-semibold text-[#344054] hover:bg-[#F9FAFB] p-1 rounded-lg transition-colors">
                      <input
                        type="checkbox"
                        checked={vendorDraft.pending.codes.includes(vendor)}
                        onChange={() => vendorDraft.toggleCode(vendor)}
                        className="w-4 h-4 accent-[#7F56D9] text-[#7F56D9] rounded border-[#D0D5DD]"
                      />
                      <span className="truncate">{vendor}</span>
                    </label>
                  ))}
                </div>
                <div className="border-t border-[#EAECF0] pt-2.5 space-y-2.5">
                  <label className="flex items-center gap-2 text-[12px] font-medium text-[#344054] cursor-pointer">
                    <input
                      type="checkbox"
                      checked={vendorDraft.pending.exclude}
                      onChange={(e) => vendorDraft.setExclude(e.target.checked)}
                      className="w-3.5 h-3.5 accent-[#7F56D9] rounded"
                    />
                    <span>Exclude selected instead</span>
                  </label>
                  <div className="flex items-center justify-between pt-1">
                    <button
                      type="button"
                      onClick={() => {
                        vendorDraft.clearCodes();
                        setVendorFilterQuery('');
                      }}
                      className="text-[13px] font-semibold text-[#667085] hover:text-[#101828] cursor-pointer"
                    >
                      Reset
                    </button>
                    <button
                      type="button"
                      onClick={() => setActivePopover(null)}
                      className="px-4 py-1.5 bg-[#7F56D9] hover:bg-[#6941C6] text-white text-[13px] font-bold rounded-[9px] shadow-xs cursor-pointer"
                    >
                      Apply
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* 6. Channel Popover */}
          <div className="flex flex-col gap-1 relative min-w-[95px]" data-popover-container>
            <div className="flex items-center">
              <label className={labelStyle}>Channel:</label>
            </div>
            <button
              type="button"
              onClick={() => togglePopover('channel')}
              className={filterBtnStyle}
            >
              <span className="truncate">{selectedChannels}</span>
              <ChevronDown className="w-3.5 h-3.5 text-[#667085] flex-shrink-0" />
            </button>

            {activePopover === 'channel' && (
              <div className="absolute left-0 top-full mt-1.5 z-50 w-52 bg-white border border-[#E4E4E7] rounded-2xl shadow-xl p-2 flex flex-col gap-0.5 max-h-60 overflow-y-auto animate-in fade-in zoom-in-95 duration-100">
                {[ALL_CHANNELS, ...channelOptionsList].map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => {
                      setSelectedChannels(c);
                      setActivePopover(null);
                    }}
                    className={`w-full px-3 py-2 text-[13px] font-semibold rounded-[9px] flex items-center justify-between text-left transition-colors cursor-pointer leading-[21px] ${selectedChannels === c ? 'bg-[#F4F0FF] text-[#7F56D9]' : 'text-[#344054] hover:bg-[#F9FAFB]'
                      }`}
                  >
                    <span className="truncate">{c}</span>
                    {selectedChannels === c && <Check className="w-4 h-4 text-[#7F56D9] flex-shrink-0" />}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* 7. Min. Sales Free-Text Number Input Filter */}
          <div className="flex flex-col gap-1 min-w-[65px]">
            <div className="flex items-center">
              <label className={labelStyle}>Min. Sales:</label>
              <InfoTooltip text="Filter to items with sales at or above this threshold." />
            </div>
            <div className="relative flex items-center">
              <input
                type="number"
                min="0"
                step="1"
                inputMode="numeric"
                value={minSales === NO_MIN_SALES || minSales === 'none' || minSales === 'Any' || !minSales ? '' : minSales}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '' || val === undefined) {
                    setMinSales(NO_MIN_SALES);
                  } else {
                    const parsed = Number(val);
                    setMinSales(Number.isFinite(parsed) && parsed >= 0 ? String(parsed) : NO_MIN_SALES);
                  }
                }}
                placeholder="$ Any"
                className="h-[34px] w-20 px-2.5 border border-[#D0D5DD] rounded-[9px] text-[13px] font-semibold text-[#344054] bg-white focus:outline-none focus:ring-2 focus:ring-[#7F56D9] shadow-2xs placeholder-[#98A2B3]"
              />
            </div>
          </div>

          {/* On-Order & OTB Span */}
          <div className="flex flex-col gap-1" data-popover-container>
            <div className="flex items-center">
              <label className={labelStyle}>On-order & OTB span:</label>
              <InfoTooltip text={`Select the time horizon for open on-order units and suggested reorders. ${ON_ORDER_OTB_SPAN_NOTE} ${ON_ORDER_NO_DATE_NOTE}`} />
            </div>
            <div className="relative">
              <button
                type="button"
                onClick={() => {
                  if (activePopover === 'on_order_span') {
                    setActivePopover(null);
                  } else {
                    setShowCustomOnOrderSpan(false);
                    togglePopover('on_order_span');
                  }
                }}
                className={filterBtnStyle}
                title={(() => {
                  const info = getSpanDateRangeInfo(onOrderSpan, undefined, onOrderStartDate, onOrderEndDate);
                  return info.fullLabel;
                })()}
              >
                {(() => {
                  const info = getSpanDateRangeInfo(onOrderSpan, undefined, onOrderStartDate, onOrderEndDate);
                  return (
                    <span className="flex items-center gap-1.5 truncate">
                      <span className="truncate">{info.label}</span>
                      <span className="text-[11px] font-normal text-[#667085]">({info.dateRange})</span>
                    </span>
                  );
                })()}
                <ChevronDown className="w-3.5 h-3.5 text-[#667085] flex-shrink-0" />
              </button>

              {activePopover === 'on_order_span' && (
                <div className="absolute left-0 top-full mt-1.5 z-50 w-72 bg-white border border-[#E4E4E7] rounded-2xl shadow-xl p-2.5 flex flex-col gap-1 animate-in fade-in zoom-in-95 duration-100">
                  {showCustomOnOrderSpan ? (
                    <div className="flex flex-col gap-3">
                      <div className="flex items-center justify-between border-b border-[#EAECF0] pb-2">
                        <span className="text-[13px] font-bold text-[#101828]">Custom span date range</span>
                        <button
                          type="button"
                          onClick={() => setShowCustomOnOrderSpan(false)}
                          className="text-xs text-[#667085] hover:text-[#101828]"
                        >
                          ✕
                        </button>
                      </div>

                      <div className="flex flex-col gap-2.5">
                        <div className="flex flex-col gap-1">
                          <label className="text-[11px] font-bold uppercase tracking-wider text-[#64748B]">Start date</label>
                          <input
                            type="date"
                            value={draftOnOrderStart}
                            max={draftOnOrderEnd || undefined}
                            onChange={(e) => setDraftOnOrderStart(e.target.value)}
                            className="h-8 w-full px-2.5 border border-[#E4E4E7] rounded-[8px] text-[12px] font-semibold text-[#344054] bg-white focus:outline-none focus:ring-2 focus:ring-[#7F56D9]"
                          />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-[11px] font-bold uppercase tracking-wider text-[#64748B]">End date</label>
                          <input
                            type="date"
                            value={draftOnOrderEnd}
                            min={draftOnOrderStart || undefined}
                            onChange={(e) => setDraftOnOrderEnd(e.target.value)}
                            className="h-8 w-full px-2.5 border border-[#E4E4E7] rounded-[8px] text-[12px] font-semibold text-[#344054] bg-white focus:outline-none focus:ring-2 focus:ring-[#7F56D9]"
                          />
                        </div>
                      </div>

                      <div className="flex items-center justify-between border-t border-[#EAECF0] pt-2">
                        <button
                          type="button"
                          onClick={() => setShowCustomOnOrderSpan(false)}
                          className="h-7 px-2.5 text-[12px] font-semibold text-[#667085] hover:text-[#101828] rounded-[7px] transition-colors"
                        >
                          Back
                        </button>
                        <button
                          type="button"
                          disabled={!draftOnOrderStart || !draftOnOrderEnd}
                          onClick={() => {
                            if (!draftOnOrderStart || !draftOnOrderEnd) return;
                            setOnOrderStartDate(draftOnOrderStart);
                            setOnOrderEndDate(draftOnOrderEnd);
                            setOnOrderSpan('custom');
                            setShowCustomOnOrderSpan(false);
                            setActivePopover(null);
                          }}
                          className="h-7 px-3 text-[12px] font-bold text-white bg-[#7F56D9] hover:bg-[#6941C6] rounded-[7px] transition-colors shadow-xs disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Apply
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {ON_ORDER_SPAN_OPTIONS.map((val) => {
                        const info = getSpanDateRangeInfo(val);
                        const isSelected = onOrderSpan === val;
                        return (
                          <button
                            key={val}
                            type="button"
                            onClick={() => {
                              setOnOrderSpan(val);
                              setActivePopover(null);
                            }}
                            className={`w-full px-3 py-2 text-[13px] rounded-[9px] flex items-center justify-between text-left transition-colors cursor-pointer leading-[20px] ${isSelected
                              ? 'bg-[#F4F0FF] text-[#7F56D9]'
                              : 'text-[#344054] hover:bg-[#F9FAFB]'
                              }`}
                          >
                            <div className="flex flex-col">
                              <span className="font-semibold">{info.label}</span>
                              <span className={`text-[11px] font-normal ${isSelected ? 'text-[#8B5CF6]' : 'text-[#667085]'}`}>
                                {info.dateRange}
                              </span>
                            </div>
                            {isSelected && <Check className="w-4 h-4 text-[#7F56D9] flex-shrink-0" />}
                          </button>
                        );
                      })}

                      {/* Custom Date Range Option */}
                      <button
                        type="button"
                        onClick={() => {
                          const today = new Date();
                          const todayIso = formatDate(today, 'yyyy-MM-dd');
                          const defaultEnd = formatDate(new Date(today.getFullYear(), today.getMonth() + 3, 0), 'yyyy-MM-dd');
                          setDraftOnOrderStart(onOrderStartDate || todayIso);
                          setDraftOnOrderEnd(onOrderEndDate || defaultEnd);
                          setShowCustomOnOrderSpan(true);
                        }}
                        className={`w-full px-3 py-2 text-[13px] rounded-[9px] flex items-center justify-between text-left transition-colors cursor-pointer leading-[20px] border-t border-[#F2F4F7] mt-0.5 ${onOrderSpan === 'custom'
                          ? 'bg-[#F4F0FF] text-[#7F56D9]'
                          : 'text-[#344054] hover:bg-[#F9FAFB]'
                          }`}
                      >
                        <div className="flex flex-col">
                          <span className="font-semibold">Custom date range...</span>
                          <span className={`text-[11px] font-normal ${onOrderSpan === 'custom' ? 'text-[#8B5CF6]' : 'text-[#667085]'}`}>
                            {onOrderSpan === 'custom' && onOrderStartDate && onOrderEndDate
                              ? getSpanDateRangeInfo('custom', undefined, onOrderStartDate, onOrderEndDate).dateRange
                              : 'Set custom start & end date'}
                          </span>
                        </div>
                        {onOrderSpan === 'custom' && <Check className="w-4 h-4 text-[#7F56D9] flex-shrink-0" />}
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Retail vs Cost basis for On Order $ and the plan pills */}
          <div className="flex flex-col gap-1 justify-end">
            <span className="text-[12px] font-medium text-transparent select-none" aria-hidden="true">&nbsp;</span>
            <div className="inline-flex items-center bg-[#F2F4F7] p-[3px] rounded-[8px] h-[34px] border border-[#E4E4E7]/60">
              <button
                type="button"
                onClick={() => setPriceBasis('retail')}
                aria-pressed={priceBasis === 'retail'}
                className={`px-3 py-1 text-[13px] font-semibold rounded-[6px] transition-all cursor-pointer h-7 flex items-center justify-center whitespace-nowrap ${priceBasis === 'retail'
                  ? 'bg-white text-[#101828] shadow-2xs font-bold'
                  : 'text-[#667085] hover:text-[#101828]'
                  }`}
              >
                Retail
              </button>
              <button
                type="button"
                onClick={() => setPriceBasis('cost')}
                aria-pressed={priceBasis === 'cost'}
                className={`px-3 py-1 text-[13px] font-semibold rounded-[6px] transition-all cursor-pointer h-7 flex items-center justify-center whitespace-nowrap ${priceBasis === 'cost'
                  ? 'bg-white text-[#101828] shadow-2xs font-bold'
                  : 'text-[#667085] hover:text-[#101828]'
                  }`}
              >
                Cost
              </button>
            </div>
          </div>
        </div>

        {/* ROW 2: Controls Toolbar (Hierarchy, Show SKUs, Weeks of Supply, Top N, YOY, Show % change, Search, Columns, Reset) */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 pt-3.5 mt-0.5 border-t border-[#EAECF0]/60">
          <div className="flex flex-wrap items-end gap-3.5 sm:gap-4.5">
            {/* Hierarchy */}
            <div className="flex flex-col gap-1" data-popover-container>
              <div className="flex items-center">
                <label className={labelStyle}>Hierarchy:</label>
                <InfoTooltip
                  text="Choose how products are grouped: one overall ranking, or Top N within each category, class, vendor or location. Every grouping except Product by Location includes Open-to-Buy."
                  align="left"
                />
              </div>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => togglePopover('hierarchy')}
                  aria-haspopup="listbox"
                  aria-expanded={activePopover === 'hierarchy'}
                  className={`h-[34px] w-[240px] px-3 rounded-[9px] text-[13px] font-semibold text-left leading-[20px] flex items-center justify-between gap-3 transition-all cursor-pointer ${activePopover === 'hierarchy'
                    ? 'border border-[#7F56D9] bg-white shadow-[0_0_0_3px_rgba(127,86,217,0.15)]'
                    : 'border border-[#D0D5DD] bg-white shadow-2xs hover:bg-[#F9FAFB]'
                    }`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="truncate text-[#344054]">{selectedHierarchyConfig?.label ?? 'Select Hierarchy'}</span>
                    {selectedHierarchyConfig?.hasOtb && (
                      <span className="px-1.5 py-0.5 rounded-[5px] bg-[#F9F5FF] text-[#6941C6] border border-[#E9D7FE] text-[10px] font-bold shrink-0">
                        OTB
                      </span>
                    )}
                  </div>
                  {activePopover === 'hierarchy' ? (
                    <ChevronUp className="w-3.5 h-3.5 text-[#7F56D9] shrink-0" />
                  ) : (
                    <ChevronDown className="w-3.5 h-3.5 text-[#667085] shrink-0" />
                  )}
                </button>

                {activePopover === 'hierarchy' && (
                  <div role="listbox" className="absolute left-0 top-[calc(100%+6px)] z-[100] w-[260px] bg-white border border-[#E4E4E7] rounded-2xl shadow-xl p-2 flex flex-col gap-0.5 animate-in fade-in zoom-in-95 duration-100">
                    {[
                      { title: 'WITH OPEN-TO-BUY', options: WITH_OTB_HIERARCHY_OPTIONS },
                      { title: 'WITHOUT OPEN-TO-BUY', options: WITHOUT_OTB_HIERARCHY_OPTIONS },
                    ].map((section, sectionIdx) => (
                      <React.Fragment key={section.title}>
                        <div className={`px-3 py-1.5 ${sectionIdx > 0 ? 'pt-2' : ''} text-[11px] font-bold text-[#98A2B3] uppercase tracking-wider`}>
                          {section.title}
                        </div>
                        {section.options.map((opt) => {
                          const isSelected = hierarchy === opt.key;
                          return (
                            <button
                              key={opt.key}
                              type="button"
                              role="option"
                              aria-selected={isSelected}
                              onClick={() => {
                                setHierarchy(opt.key);
                                setActivePopover(null);
                              }}
                              className={`w-full px-3 py-2 text-[13px] font-semibold rounded-[9px] flex items-center justify-between text-left transition-colors cursor-pointer leading-[20px] ${isSelected
                                ? 'bg-[#F9F5FF] text-[#6941C6]'
                                : 'text-[#344054] hover:bg-[#F4F4F5]'
                                }`}
                            >
                              <span className="truncate">{opt.label}</span>
                              <div className="flex items-center gap-2 shrink-0">
                                {opt.hasOtb && (
                                  <span className="px-1.5 py-0.5 rounded-[5px] bg-[#F9F5FF] text-[#6941C6] border border-[#E9D7FE] text-[10px] font-bold">
                                    OTB
                                  </span>
                                )}
                                {isSelected && <Check className="w-3.5 h-3.5 text-[#6941C6]" />}
                              </div>
                            </button>
                          );
                        })}
                      </React.Fragment>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Show SKUs */}
            <div className="flex flex-col gap-1">
              <div className="flex items-center">
                <label className={labelStyle}>Show SKUs:</label>
                <InfoTooltip text="Product shows one rolled-up row per product. SKU lets you expand each product into its individual SKUs." />
              </div>
              <div role="radiogroup" aria-label="Show SKUs" className="inline-flex h-[34px] p-[3px] rounded-[9px] border border-[#D0D5DD] bg-[#F9FAFB] shadow-2xs">
                {(['product', 'sku'] as const).map((grain) => {
                  const isActive = skuGrain === grain;
                  return (
                    <button
                      key={grain}
                      type="button"
                      role="radio"
                      aria-checked={isActive}
                      onClick={() => setSkuGrain(grain)}
                      className={`px-3 rounded-[7px] text-[13px] font-semibold leading-[20px] transition-all duration-150 cursor-pointer ${isActive
                        ? 'bg-white text-[#182230] shadow-[0_1px_3px_rgba(16,24,40,0.1),0_1px_2px_rgba(16,24,40,0.06)]'
                        : 'text-[#667085] hover:text-[#344054]'
                        }`}
                    >
                      {grain === 'product' ? 'Product' : 'SKU'}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Weeks of Supply target -- drives Suggested Reorder (input field + options) */}
            <div className="flex flex-col gap-1" data-popover-container>
              <div className="flex items-center">
                <label className={labelStyle}>Weeks of Supply:</label>
                <InfoTooltip text="Target used to calculate Suggested Reorder: a number of weeks, or a cover-through date (e.g. a product's out-date), where weeks = (date - today) / 7." />
              </div>
              <div className="flex items-center gap-2">
                <div role="radiogroup" aria-label="Reorder target" className="inline-flex h-[34px] p-[3px] rounded-[9px] border border-[#D0D5DD] bg-[#F9FAFB] shadow-2xs">
                  {(['weeks', 'date'] as const).map((mode) => {
                    const isActive = reorderTargetMode === mode;
                    return (
                      <button
                        key={mode}
                        type="button"
                        role="radio"
                        aria-checked={isActive}
                        onClick={() => setReorderTargetMode(mode)}
                        className={`px-3 rounded-[7px] text-[13px] font-semibold leading-[20px] transition-all duration-150 cursor-pointer ${isActive
                          ? 'bg-white text-[#182230] shadow-[0_1px_3px_rgba(16,24,40,0.1),0_1px_2px_rgba(16,24,40,0.06)]'
                          : 'text-[#667085] hover:text-[#344054]'
                          }`}
                      >
                        {mode === 'weeks' ? 'Weeks' : 'Date'}
                      </button>
                    );
                  })}
                </div>
                {reorderTargetMode === 'weeks' ? (
                  <div className="relative flex items-center">
                    <div className="flex items-center h-[34px] bg-white border border-[#D0D5DD] rounded-[9px] shadow-2xs focus-within:ring-2 focus-within:ring-[#7F56D9] focus-within:border-transparent transition-all">
                      <input
                        type="number"
                        min="1"
                        max="104"
                        step="1"
                        inputMode="numeric"
                        value={weeksOfSupplyTarget || ''}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === '') {
                            setWeeksOfSupplyTarget(0);
                          } else {
                            const parsed = parseInt(val, 10);
                            if (!isNaN(parsed) && parsed >= 0) {
                              setWeeksOfSupplyTarget(parsed);
                            }
                          }
                        }}
                        onBlur={() => {
                          if (!weeksOfSupplyTarget || weeksOfSupplyTarget < 1) {
                            setWeeksOfSupplyTarget(DEFAULT_WEEKS_OF_SUPPLY_TARGET);
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            (e.target as HTMLInputElement).blur();
                          }
                        }}
                        placeholder="8"
                        aria-label="Weeks of Supply target"
                        className="w-11 pl-2.5 pr-0.5 py-1 text-[13px] font-semibold text-[#344054] bg-transparent focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none text-center"
                      />
                      <span className="text-[12px] font-medium text-[#667085] pr-1.5 select-none">wks</span>
                      <button
                        type="button"
                        onClick={() => togglePopover('weeks_of_supply')}
                        aria-label="Weeks of Supply options"
                        className="h-full px-1.5 border-l border-[#EAECF0] hover:bg-[#F9FAFB] rounded-r-[9px] flex items-center justify-center text-[#667085] hover:text-[#344054] cursor-pointer transition-colors"
                      >
                        <ChevronDown className="w-3.5 h-3.5" />
                      </button>
                    </div>

                    {activePopover === 'weeks_of_supply' && (
                      <div className="absolute left-0 top-full mt-1.5 z-50 w-32 bg-white border border-[#E4E4E7] rounded-2xl shadow-xl p-1.5 flex flex-col gap-0.5 animate-in fade-in zoom-in-95 duration-100">
                        <div className="px-2 py-1 text-[11px] font-semibold text-[#667085] uppercase tracking-wider">Presets</div>
                        {WEEKS_OF_SUPPLY_OPTIONS.map((weeks) => (
                          <button
                            key={weeks}
                            type="button"
                            onClick={() => {
                              setWeeksOfSupplyTarget(weeks);
                              setActivePopover(null);
                            }}
                            className={`w-full px-3 py-1.5 text-[13px] font-semibold rounded-[8px] flex items-center justify-between text-left transition-colors cursor-pointer ${weeksOfSupplyTarget === weeks ? 'bg-[#F4F0FF] text-[#7F56D9]' : 'text-[#344054] hover:bg-[#F9FAFB]'
                              }`}
                          >
                            <span>{weeks} weeks</span>
                            {weeksOfSupplyTarget === weeks && <Check className="w-4 h-4 text-[#7F56D9]" />}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                ) : (
                  <div className="flex items-center gap-2">
                    <input
                      type="date"
                      value={coverThroughDate}
                      min={formatDate(addDays(today, 1), 'yyyy-MM-dd')}
                      onChange={(e) => setCoverThroughDate(e.target.value)}
                      aria-label="Cover-through date"
                      className="h-[34px] px-2.5 border border-[#D0D5DD] rounded-[9px] text-[13px] font-semibold text-[#344054] bg-white shadow-2xs focus:outline-none focus:ring-2 focus:ring-[#7F56D9]"
                    />
                    <span data-testid="cover-date-weeks" className="text-[12px] font-medium text-[#667085] whitespace-nowrap">
                      {coverDateWeeks !== null
                        ? `= ${formatWeeks(coverDateWeeks)} wks`
                        : `Pick a future date (using ${weeksOfSupplyTarget} wks)`}
                    </span>
                  </div>
                )}
              </div>
            </div>


            {/* Top N Custom Popover */}
            <div className="flex flex-col gap-1" data-popover-container>
              <div className="flex items-center">
                <label className={labelStyle}>Top N:</label>
                <InfoTooltip text="Number of top-performing items to display based on the active ranking metric." />
              </div>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => togglePopover('top_n')}
                  className="h-[34px] bg-white border border-[#D0D5DD] rounded-[9px] px-2.5 text-[13px] font-semibold text-[#344054] flex items-center gap-1.5 shadow-2xs hover:bg-[#F9FAFB] transition-colors cursor-pointer"
                >
                  <span>{topN}</span>
                  <ChevronDown className="w-3.5 h-3.5 text-[#667085]" />
                </button>

                {activePopover === 'top_n' && (
                  <div className="absolute left-0 top-full mt-1.5 z-50 w-28 bg-white border border-[#E4E4E7] rounded-2xl shadow-xl p-1.5 flex flex-col gap-0.5 animate-in fade-in zoom-in-95 duration-100">
                    {[10, 25, 50, 100, 250, 500].map((num) => (
                      <button
                        key={num}
                        type="button"
                        onClick={() => {
                          setTopN(num);
                          setActivePopover(null);
                        }}
                        className={`w-full px-3 py-1.5 text-[13px] font-semibold rounded-[8px] flex items-center justify-between text-left transition-colors cursor-pointer ${topN === num ? 'bg-[#F4F0FF] text-[#7F56D9]' : 'text-[#344054] hover:bg-[#F9FAFB]'
                          }`}
                      >
                        <span>{num}</span>
                        {topN === num && <Check className="w-4 h-4 text-[#7F56D9]" />}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>


            {/* YOY Comparison Toggle */}
            <div className="flex flex-col gap-1 items-start">
              <div className="flex items-center">
                <label className={labelStyle}>YOY Comparison:</label>
                <InfoTooltip text="Compare performance against the same period last year." />
              </div>
              <div className="h-[34px] flex items-center">
                <button
                  type="button"
                  onClick={() => setYoyComparison(!yoyComparison)}
                  className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${yoyComparison ? 'bg-[#7F56D9]' : 'bg-[#D0D5DD]'
                    }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${yoyComparison ? 'translate-x-4' : 'translate-x-0'
                      }`}
                  />
                </button>
              </div>
            </div>

            {/* Show % Change Toggle - only visible when YOY Comparison is ON */}
            {yoyComparison && (
              <div className="flex flex-col gap-1 items-start animate-in fade-in duration-150">
                <div className="flex items-center">
                  <label className={labelStyle}>Show % change</label>
                  <InfoTooltip text="Switch the Change row between a dollar/unit difference and a percent change vs. last year." />
                </div>
                <div className="h-[34px] flex items-center">
                  <button
                    type="button"
                    onClick={() => setShowPctChange(!showPctChange)}
                    className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${showPctChange ? 'bg-[#7F56D9]' : 'bg-[#D0D5DD]'
                      }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${showPctChange ? 'translate-x-4' : 'translate-x-0'
                        }`}
                    />
                  </button>
                </div>
              </div>
            )}

            {activeView === 'by_sku' && (
              <div className="flex flex-col gap-1 items-start">
                <div className="flex items-center">
                  <label className={labelStyle}>Hide zero-sales SKUs</label>
                  <InfoTooltip text="Hide SKUs with no units sold in the period. Product totals still include them." />
                </div>
                <div className="h-[34px] flex items-center">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={hideZeroSalesSkus}
                    aria-label="Hide zero-sales SKUs"
                    onClick={() => setHideZeroSalesSkus(!hideZeroSalesSkus)}
                    className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${hideZeroSalesSkus ? 'bg-[#7F56D9]' : 'bg-[#D0D5DD]'
                      }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${hideZeroSalesSkus ? 'translate-x-4' : 'translate-x-0'
                        }`}
                    />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Right Side: Search Mode Dropdown + Search Box + Columns Toggle + Reset */}
          <div className="flex items-center gap-2">
            {/* Search Mode Custom Popover Dropdown */}
            <div className="relative flex items-center gap-1" data-popover-container>
              <button
                type="button"
                onClick={() => togglePopover('search_mode')}
                className="h-[34px] min-w-[130px] bg-white border border-[#D0D5DD] rounded-[9px] px-[14px] text-[14px] font-semibold text-[#344054] text-left leading-[21px] flex items-center justify-between gap-[6px] shadow-2xs hover:bg-[#F9FAFB] transition-colors cursor-pointer"
              >
                <span>
                  {searchMode === 'product_name' ? 'Product name' : searchMode === 'tag' ? 'Tag' : 'SKU / Barcode'}
                </span>
                <ChevronDown className="w-3.5 h-3.5 text-[#667085]" />
              </button>

              {activePopover === 'search_mode' && (
                <div className="absolute right-0 top-11 z-50 w-44 bg-white border border-[#E4E4E7] rounded-2xl shadow-xl p-2 flex flex-col gap-0.5 animate-in fade-in zoom-in-95 duration-100">
                  {[
                    { label: 'Product name', key: 'product_name' },
                    { label: 'Tag', key: 'tag' },
                    { label: 'SKU / Barcode', key: 'sku' },
                  ].map((opt) => (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => {
                        setSearchMode(opt.key as SearchFieldMode);
                        setActivePopover(null);
                      }}
                      className={`w-full px-3 py-2 text-[13px] font-semibold rounded-[9px] flex items-center justify-between text-left transition-colors cursor-pointer leading-[21px] ${searchMode === opt.key ? 'bg-[#F4F0FF] text-[#7F56D9]' : 'text-[#344054] hover:bg-[#F9FAFB]'
                        }`}
                    >
                      <span>{opt.label}</span>
                      {searchMode === opt.key && <Check className="w-4 h-4 text-[#7F56D9]" />}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Search Input Box */}
            <div className="relative flex items-center">
              <Search className="w-4 h-4 text-[#667085] absolute left-3 top-2.5 pointer-events-none" />
              <input
                type="text"
                placeholder={
                  searchMode === 'product_name'
                    ? 'Search product name...'
                    : searchMode === 'tag'
                      ? 'Search tags...'
                      : 'Search SKU / barcode...'
                }
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-[34px] pl-9 pr-7 text-[13px] font-medium bg-white border border-[#D0D5DD] rounded-[9px] focus:outline-none focus:ring-2 focus:ring-[#7F56D9] w-48 shadow-2xs text-[#344054] placeholder-[#98A2B3]"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2.5 top-2 text-[#98A2B3] hover:text-[#344054] cursor-pointer"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* VISIBLE COLUMNS Custom Popover */}
            <div className="relative flex items-center gap-1" data-popover-container>
              <button
                type="button"
                onClick={() => togglePopover('visible_columns')}
                className="h-[34px] px-[14px] bg-white border border-[#D0D5DD] rounded-[9px] text-[14px] font-semibold text-[#344054] hover:bg-[#F9FAFB] shadow-2xs flex items-center gap-1.5 transition-colors leading-[21px] cursor-pointer"
              >
                <SlidersHorizontal className="w-3.5 h-3.5 text-[#667085]" />
                <span>Columns</span>
              </button>

              {activePopover === 'visible_columns' && (
                <div className="absolute right-0 top-11 z-[60] w-72 bg-white rounded-2xl shadow-xl border border-[#E4E4E7] p-4 space-y-3 animate-in fade-in zoom-in-95 duration-100">
                  <div className="flex items-center justify-between border-b border-[#EAECF0] pb-2">
                    <h4 className="text-[13px] font-bold text-[#101828]">Visible Columns</h4>
                    <button onClick={() => setActivePopover(null)} className="text-[#98A2B3] hover:text-[#344054] cursor-pointer">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                    {orderedColumns.map((col) => {
                      const isAvailable = isColumnAvailableInView(col, activeView);
                      return (
                        <label
                          key={col.key}
                          {...getColumnDragProps(col.key, 'y')}
                          title={isAvailable ? undefined : 'Available at SKU level only'}
                          className={`flex items-start gap-2.5 text-[13px] text-[#344054] p-1.5 rounded-lg transition-colors ${isAvailable ? 'cursor-pointer hover:bg-[#F9FAFB]' : 'cursor-not-allowed opacity-50'} ${dragClass(col.key, 'y')}`}
                        >
                          {isPinned(col.key) ? (
                            <Pin className="w-3.5 h-3.5 mt-0.5 text-[#7C3AED] shrink-0" aria-label="Pinned" />
                          ) : (
                            <GripVertical className="w-3.5 h-3.5 mt-0.5 text-[#98A2B3] cursor-grab shrink-0" aria-hidden="true" />
                          )}
                          <input
                            type="checkbox"
                            checked={isColumnVisibleInView(col, activeView, visibleColumns)}
                            disabled={!isAvailable}
                            onChange={(e) =>
                              setVisibleColumns({
                                ...visibleColumns,
                                [col.key]: e.target.checked,
                              })
                            }
                            className="w-4 h-4 accent-[#7F56D9] text-[#7F56D9] rounded border-[#D0D5DD] mt-0.5 disabled:cursor-not-allowed"
                          />
                          <div className="space-y-0.5">
                            <div className="font-semibold text-[#101828]">{col.label}</div>
                            <div className="text-[11px] text-[#667085] leading-tight">{col.description}</div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                  <div className="border-t border-[#EAECF0] pt-2 text-[11px] text-[#98A2B3] flex items-start justify-between gap-3">
                    <span>Drag column headers, or the items above, to reorder. Drag a header's right edge to resize. Saved for you on this browser.</span>
                    <div className="shrink-0 flex flex-col items-end gap-1">
                      <button
                        type="button"
                        onClick={resetColumnOrderAndPins}
                        className="font-semibold text-[#7F56D9] hover:text-[#6941C6] cursor-pointer"
                      >
                        Reset order
                      </button>
                      <button
                        type="button"
                        onClick={resetColumnWidths}
                        className="font-semibold text-[#7F56D9] hover:text-[#6941C6] cursor-pointer"
                      >
                        Reset widths
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Reset Filters Icon Button */}
            <button
              type="button"
              onClick={handleResetFilters}
              title="Reset Filters"
              className="h-[34px] w-[34px] bg-white border border-[#D0D5DD] rounded-[9px] text-[#667085] hover:text-[#09090B] hover:bg-[#F9FAFB] shadow-2xs flex items-center justify-center transition-colors cursor-pointer"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* --- 4. SUMMARY ROW (ROW 3) --- */}
      <div className="flex items-center gap-1.5 text-[13px] text-[#71717A] px-1 py-0.5">
        <span>
          Top <strong className="text-[#09090B] font-semibold">{mappedItems.length} items</strong>
          {hierarchy !== 'total' && (
            <>
              {' '}across <strong className="text-[#09090B] font-semibold">{hierarchyGroups.length} {HIERARCHY_GROUP_NOUN[hierarchy].toLowerCase()} groups</strong> (top {topN} per group)
            </>
          )}
          {' '}&middot; sorted by{' '}
          <strong className="text-[#09090B] font-semibold">{sortSummaryLabel(activeSort)}</strong>
        </span>
        <InfoTooltip text="Items currently displayed in this view sorted by active column." />
      </div>

      {/* --- LOAD ERROR BANNER --- */}
      {loadError && (
        <div className="flex items-center gap-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span>{loadError}</span>
        </div>
      )}

      {/* --- 4. TABLE --- */}
      <div className="bg-white border border-[#E4E4E7] rounded-2xl shadow-2xs overflow-hidden mt-0.5 relative z-10">
        <div ref={scrollRef} onScroll={updateScrollState} className="overflow-x-auto max-h-[calc(100vh-220px)] overflow-y-auto">
          <table className="w-full text-left text-[13px] border-separate border-spacing-0">
            <thead ref={theadRef} className={`sticky top-0 ${STICKY_LAYER.header} bg-[#F9F9FB]`}>
              <tr className="bg-[#F9F9FB] text-[#64748B] font-semibold select-none">
                {/* Sticky Column: Rank / Product */}
                <th
                  style={{ width: widthOf(PRODUCT_COLUMN_KEY), minWidth: widthOf(PRODUCT_COLUMN_KEY) }}
                  className={`sticky left-0 top-0 bg-[#F9F9FB] ${activeMenuColumn === PRODUCT_COLUMN_KEY ? STICKY_LAYER.menuOpen : STICKY_LAYER.headerFirstColumn} hover:z-50 px-3.5 py-2.5 border-r border-b border-[#E4E4E7] ${headShadow || FIRST_COL_SHADOW} text-left`}
                >
                  <SortableHeader
                    label={productColumnHeader}
                    sortKey={PRODUCT_COLUMN_KEY}
                    activeSort={activeSort}
                    isMenuOpen={activeMenuColumn === PRODUCT_COLUMN_KEY}
                    onToggleMenu={() => toggleColumnMenu(PRODUCT_COLUMN_KEY)}
                    // The sticky product column can only be sorted -- no pin or hide.
                    menu={
                      <ColumnHeaderMenu
                        title={productColumnHeader}
                        description={PRODUCT_COLUMN_DESCRIPTION}
                        sortType="text"
                        activeDirection={activeSort.key === PRODUCT_COLUMN_KEY ? activeSort.direction : null}
                        onSort={(direction) => handleSort(PRODUCT_COLUMN_KEY, direction)}
                        onClose={closeColumnMenu}
                      />
                    }
                    align="left"
                    description={PRODUCT_COLUMN_DESCRIPTION}
                  />
                  <span {...getResizeHandleProps(PRODUCT_COLUMN_KEY, 'Product')} className={COLUMN_RESIZE_HANDLE_CLASS} />
                </th>

                {visibleOrderedColumns.map((col, idx) => {
                  const isMenuOpen = activeMenuColumn === col.key;
                  const isLastColumn = idx >= visibleOrderedColumns.length - 2;
                  const pinned = isPinned(col.key);
                  return (
                    <th
                      key={col.key}
                      {...getColumnDragProps(col.key, 'x')}
                      draggable={!isResizing && !pinned && !isMenuOpen}
                      style={{ width: widthOf(col.key), minWidth: widthOf(col.key) }}
                      className={`${col.padX} py-2.5 sticky top-0 bg-[#F9F9FB] ${isMenuOpen ? STICKY_LAYER.menuOpen : STICKY_LAYER.header} hover:z-50 border-b border-[#E4E4E7] ${headShadow} ${ALIGN_CLASS[col.align]} ${pinned ? '' : 'cursor-grab'} ${dragClass(col.key, 'x')}`}
                    >
                      <SortableHeader
                        label={headerLabelOf(col)}
                        sortKey={col.key}
                        activeSort={activeSort}
                        isMenuOpen={isMenuOpen}
                        onToggleMenu={() => toggleColumnMenu(col.key)}
                        menu={
                          <ColumnHeaderMenu
                            title={headerLabelOf(col)}
                            description={col.description}
                            sortType={sortTypeOf(col.key)}
                            activeDirection={activeSort.key === col.key ? activeSort.direction : null}
                            onSort={(direction) => handleSort(col.key, direction)}
                            pin={{ isPinned: pinned, onToggle: () => togglePinnedColumn(col.key) }}
                            onHide={() => setVisibleColumns({ ...visibleColumns, [col.key]: false })}
                            onClose={closeColumnMenu}
                            align={isLastColumn ? 'right' : 'left'}
                          />
                        }
                        align={col.align}
                        description={col.description}
                        isLastColumn={isLastColumn}
                      />
                      <span {...getResizeHandleProps(col.key, headerLabelOf(col))} className={COLUMN_RESIZE_HANDLE_CLASS} />
                    </th>
                  );
                })}
              </tr>
            </thead>

            {loading ? (
              <tbody>
                <tr>
                  <td colSpan={visibleOrderedColumns.length + 1} className="px-4 py-10 text-center text-[#71717A] text-[13px]">
                    <div className="inline-flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin text-[#7F56D9]" />
                      <span>Loading top sellers&hellip;</span>
                    </div>
                  </td>
                </tr>
              </tbody>
            ) : mappedItems.length === 0 ? (
              <tbody>
                <tr>
                  <td colSpan={visibleOrderedColumns.length + 1} className="px-4 py-10 text-center text-[#71717A] text-[13px]">
                    No items match the current filters.
                  </td>
                </tr>
              </tbody>
            ) : isFlatListView ? (
              <tbody>
                {/* Pinned under the header. */}
                {otb && (
                  <tr className="bg-[#F8F9FA]" data-testid="top-sellers-total-store-plan-row">
                    <td
                      className={`sticky left-0 ${STICKY_LAYER.pinnedRowFirstColumn} px-3.5 py-2.5 bg-[#F8F9FA] border-r border-b border-[#E4E4E7] ${headShadow || FIRST_COL_SHADOW} whitespace-nowrap`}
                      style={stickyTop}
                    >
                      <span className="text-[13px] font-bold text-[#6D28D9]">Total Store Plan & OTB</span>
                    </td>
                    <td
                      colSpan={visibleOrderedColumns.length}
                      className={`sticky ${STICKY_LAYER.pinnedRowCell} px-4 py-2.5 bg-[#F8F9FA] ${ROW_DIVIDER} ${headShadow}`}
                      style={stickyTop}
                    >
                      <OtbSummaryPills
                        layout="inline"
                        otbLabel="Total OTB"
                        priceBasis={priceBasis}
                        planSales={priceBasis === 'cost' ? otb.total.plan_sales_cost : otb.total.plan_sales_retail}
                        planInflow={priceBasis === 'cost' ? otb.total.plan_inflow_cost : otb.total.plan_inflow_retail}
                        onOrder={priceBasis === 'cost' ? otb.total.on_order_cost : otb.total.on_order_retail}
                        otb={priceBasis === 'cost' ? otb.total.otb_cost : otb.total.otb_retail}
                      />
                    </td>
                  </tr>
                )}
                {sortedTotalStoreItems.map((item) => renderItemRows(item))}
              </tbody>
            ) : (
                sortedHierarchyGroups.map((group) => {
                  const groupStateKey = `${hierarchy}:${group.key}`;
                  const isGroupExpanded = expandedGroups[groupStateKey] ?? true;
                  const groupOtb = otbForGroup(otb, group.key, hierarchy);
                  const shadow = groupShadow(groupStateKey);
                  const pinnedBg = 'bg-[#F8F9FA] group-hover:bg-[#F2F4F7]';
                  return (
                    <tbody key={groupStateKey} ref={registerGroupBody(groupStateKey)}>
                      {/* Group header, pinned under the header: summed totals per visible column -- see sumTopSellerMetrics(). */}
                      <tr
                        onClick={() =>
                          setExpandedGroups({
                            ...expandedGroups,
                            [groupStateKey]: !isGroupExpanded,
                          })
                        }
                        className="group bg-[#F8F9FA] hover:bg-[#F2F4F7] cursor-pointer transition-colors font-semibold"
                        data-testid="top-sellers-group-row"
                      >
                        <td
                          className={`sticky left-0 ${STICKY_LAYER.pinnedRowFirstColumn} ${pinnedBg} px-3.5 py-2.5 border-r border-b border-[#E4E4E7] ${shadow || FIRST_COL_SHADOW} whitespace-nowrap`}
                          style={stickyTop}
                        >
                          <div className="flex flex-col gap-1.5">
                            <div className="flex items-center gap-2">
                              {isGroupExpanded ? (
                                <ChevronDown className="w-4 h-4 text-[#344054]" />
                              ) : (
                                <ChevronRight className="w-4 h-4 text-[#344054]" />
                              )}
                              {group.classCode && (
                                <span className="px-1.5 py-0.5 rounded-[5px] bg-[#F2F4F7] text-[11px] font-mono font-semibold text-[#667085] shrink-0">
                                  {group.classCode}
                                </span>
                              )}
                              <span style={RANK_PRODUCT_CELL_STYLE} className="text-[13px] font-bold text-[#101828]">{group.label}</span>
                              <span className="text-[12px] text-[#667085] font-normal">({group.count} items)</span>
                            </div>
                            {/* Plan figures for the whole category / class, not just the Top N rows shown. */}
                            {groupOtb && (
                              <OtbSummaryPills
                                priceBasis={priceBasis}
                                planSales={priceBasis === 'cost' ? groupOtb.plan_sales_cost : groupOtb.plan_sales_retail}
                                planInflow={priceBasis === 'cost' ? groupOtb.plan_inflow_cost : groupOtb.plan_inflow_retail}
                                onOrder={priceBasis === 'cost' ? groupOtb.on_order_cost : groupOtb.on_order_retail}
                                otb={priceBasis === 'cost' ? groupOtb.otb_cost : groupOtb.otb_retail}
                                className="pl-6"
                              />
                            )}
                          </div>
                        </td>
                        {renderCells({ kind: 'group', group }, `${ROW_DIVIDER} sticky ${STICKY_LAYER.pinnedRowCell} ${pinnedBg} ${shadow}`, stickyTop)}
                      </tr>

                      {isGroupExpanded && group.items.map((item) => renderItemRows(item))}
                    </tbody>
                  );
                })
            )}

            {/* Pinned to the bottom: every listed item, whatever the filters, search and collapsed groups. */}
            {!loading && mappedItems.length > 0 && (
              <tfoot className={`sticky bottom-0 ${STICKY_LAYER.footer}`}>
                <tr className="bg-[#F2F4F7]" data-testid="top-sellers-total-row">
                  <td
                    className={`sticky left-0 ${STICKY_LAYER.footerFirstColumn} px-3.5 py-2.5 bg-[#F2F4F7] border-r border-t border-[#E4E4E7] ${footShadow || FIRST_COL_SHADOW} whitespace-nowrap`}
                  >
                    <span className="text-[13px] font-bold text-[#101828]">Total</span>
                    <span className="ml-1.5 text-[12px] text-[#667085] font-normal">{mappedItems.length} items</span>
                  </td>
                  {renderCells({ kind: 'group', group: totalRow }, `bg-[#F2F4F7] border-t border-[#E4E4E7] ${footShadow}`)}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* --- 5. FOOTER SPEC NOTE --- */}
      <div className="flex items-center gap-1.5 text-[12px] text-[#71717A] pt-2 border-t border-[#E4E4E7]">
        <Info className="w-4 h-4 text-[#7F56D9] flex-shrink-0" />
        <span>
          Rank is auto-calculated from the current &ldquo;Rank by&rdquo; metric. Suggested Reorder covers the selected Weeks of Supply at the current sales rate, net of on-hand and on-order &mdash; a starting point, not a locked recommendation.
        </span>
      </div>
    </div>
  );
};

export default TopSellersReport;